import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, Redirect } from 'react-router-dom';
import { GlobalContext } from 'providers/GlobalProvider';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SegmentIcon from '@mui/icons-material/Segment';
import CommentIcon from '@mui/icons-material/Comment';
import { Balance, AttachMoney } from '@mui/icons-material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DifferenceIcon from '@mui/icons-material/Difference';
import { CircularProgress, Modal } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddBoxIcon from '@mui/icons-material/AddBox';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import RttIcon from '@mui/icons-material/Rtt';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';

import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import BarGraph from '../common/BarGraph';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  parseDateOnlyToLocalDate,
  formatLocalDateToDateOnly,
} from 'utils/dateFormater';
import {
  useCreateComment,
  useDeleteComment,
  useGetCommentsByCommentableId,
} from 'api/bitedb/comment/comment.hooks';
import {
  useUpdateProductStatus,
  useGetProductsByProductId,
  useGetProductsByHTSCode,
  useUpdateProduct,
} from 'api/bitedb/products/products.hooks';
import {
  useGetScreeningHistoryByProductId,
  useRunDatabaseScreening,
  useRunProductScreening,
  useToggleProductDocVisibility,
  useGetDatabaseScreeningStatus,
  screeningKeys,
} from 'api/bitedb/screeningHistory/screening.hooks';
import { fetchProductCrossRulings } from 'api/bitedb/screeningHistory/screening.service';
import {
  getProductById,
  listProductCategories,
} from 'api/bitedb/products/products.service';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetAuditLogsByProductId,
  useGetAuditLogsByDatabaseId,
} from 'api/bitedb/audit/audit.hooks';
import AuditLogRow from '../../Monitor/ActivityTab/AuditLogRow';

import pako from 'pako';
import { Buffer } from 'buffer';
import { enqueueSnackbar } from 'notistack';
import TariffResultsComp from '../Components/TariffResultsCompProduct';
import TariffHistoryModal from './TariffHistoryModal';
import { getDateDifference } from 'utils/dateFormater';
import { format } from 'date-fns';
import moment from 'moment';
import ShareModal from '../Components/ShareModal';
import ChangeStatus from './ChangeStatus';
import { Form, Formik } from 'formik';
import {
  IProductCategory,
  PaginationParams,
} from 'api/bitedb/products/products';

import LensesTable from '../Tables/LensesTable';
import LensColumns from '../Tables/LensColumns';

import { productTicketPDF } from 'components/Common/PDFKitManage';
import { TextField } from '@mui/material';
import Select from '@mui/material/Select';
import { styled } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import ComplianceScoringGuidelines from 'components/BiteDB/Components/ComplianceScoringGuidelines';

import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { countries } from 'utils/countriesWithCodes';

import MasterTable from '../Tables/MasterTable';
import UploadColumns, { ColumnKeys } from '../Tables/UploadColumns';
import UploadRow from '../Tables/UploadRow';
import FileAPI from 'api/FileAPI';
import { AddUploadModal } from './AddUploadModal';
import DatahubAPI from 'api/DatahubAPI';

interface DashboardProps {
  supplier: any;
}

// Constants
const AUDIT_ITEMS_PER_PAGE = 5;

const SCORE_DEDUCTIONS: any = {
  'Risk Level Medium/High': '-10',
  'Missing HTS Code': '-10',
  'Invalid HTS Code': '-10',
  'Missing Country of Origin': '-10',
  'HTS Document Unverified': '-5',
};

const fetchDeduction = (flag: string) => {
  const deduction = SCORE_DEDUCTIONS[flag];
  return deduction ?? '0';
};

const Results: React.FC<DashboardProps> = (props: any) => {
  const history = useHistory();
  const { cur_user, userOpts } = useContext(GlobalContext);

  const [userHasMonitor, setUserHasMonitor] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(true);

  const requiredTiers = ['business', 'enterprise', 'database'];

  const checkSubscription = async () => {
    if (cur_user.quickcodeOnly) {
      setUserHasMonitor(true);
      setIsCheckingSubscription(false);
      return;
    }
    try {
      if (
        requiredTiers.some((role) =>
          cur_user.selected_team?.attributes?.tier.includes(role),
        )
      ) {
        setUserHasMonitor(true);
        setIsCheckingSubscription(false);
        return;
      }
      if (requiredTiers.includes(cur_user.license)) {
        setUserHasMonitor(true);
        setIsCheckingSubscription(false);
        return;
      }
    } finally {
      setIsCheckingSubscription(false);
    }
  };

  useEffect(() => {
    if (cur_user.selected_team) {
      checkSubscription();
    }
  }, [cur_user.selected_team]);

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  const handleClearChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    flag: string,
  ) => {
    const newValue = event.target.checked;
    // Here you would typically call an API or update a parent state
    let flags = product?.clear_flags ?? [];
    const isPresent = flags.includes(flag);
    if (newValue) {
      if (isPresent) {
        flags = flags.filter((f: string) => f !== flag);
      }
    } else {
      if (!isPresent) {
        flags.push(flag);
      }
    }
    product.clear_flags = flags;
    handleDetailsSave();
  };

  const [htsOptionsAdditional, setHtsOptionsAdditional] = React.useState<
    Record<string, any[]>
  >({});
  const [htsOptions, setHtsOptions] = React.useState<any[]>([]);
  const [isHtsLoading, setIsHtsLoading] = React.useState(false);
  const [fetchedPrefix, setFetchedPrefix] = React.useState<string>('');
  const formatHtsPretty = (raw: string) => {
    const d = raw.replace(/\D/g, '');
    const parts = [
      d.slice(0, 4),
      d.slice(4, 6),
      d.slice(6, 8),
      d.slice(8, 10),
    ].filter(Boolean);
    return parts.join('.');
  };

  const filterHtsOptions = createFilterOptions<any>({
    matchFrom: 'any',
    stringify: (option: any) => option.code + option.label, // Allows searching by raw digits or dots
  });

  const [openRules, setOpenRules] = useState(false);
  const handleCloseRules = () => setOpenRules(false);

  const { supplier } = props;

  const [product, setProduct] = useState<any>(supplier);

  const [productCategories, setProductCategories] = useState<
    IProductCategory[]
  >([]);

  useEffect(() => {
    listProductCategories(cur_user?.selected_team?.id || '-1').then(
      (categories) => setProductCategories(categories),
    );
  }, [supplier]);

  const handleChange = (field: string, value: any) => {
    setProduct((prev: any) => ({ ...prev, [field]: value }));
  };

  const [countryCounts, setCountryCounts] = useState<Record<string, number>>(
    {} as Record<string, number>,
  );
  const [countryValues, setCountryValues] = useState<Record<string, number>>(
    {} as Record<string, number>,
  );

  type COpt =
    | string
    | {
        name?: string;
        label?: string;
        countryName?: string;
        iso2?: string;
        alpha2?: string;
        alpha2Code?: string;
        usCustomsCountryCode?: string;
        country?: {
          name?: string;
          countryName?: string;
          iso2?: string;
          alpha2?: string;
          alpha2Code?: string;
        };
      };

  const getName = (o: COpt) => (typeof o === 'string' ? o : o?.name ?? '');
  const getIso = (o: COpt) =>
    typeof o === 'string' ? '' : o?.iso2 ?? ''?.toUpperCase();
  const displayLabel = (o: COpt) => {
    const n = getName(o);
    const code = getIso(o);
    return code ? `${n} - ${code}` : n;
  };

  const findCountryOption = (valueIsoOrName: string | undefined | null) => {
    if (!valueIsoOrName) {
      return null;
    }
    const v = String(valueIsoOrName).toUpperCase();
    return (
      (countries as COpt[]).find((c) => getIso(c)?.toUpperCase() === v) ||
      (countries as COpt[]).find((c) => getName(c).toUpperCase() === v) ||
      null
    );
  };

  // Run screening mutation
  const runScreeningMutation = useRunDatabaseScreening();
  const runProductScreeningMutation = useRunProductScreening();
  const [isLoading, setIsLoading] = useState(false);
  const [trigger, setTrigger] = useState(false);
  const [removedDocIds, setRemovedDocIds] = useState<string[]>([]);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const downloadRef = useRef<HTMLDivElement>(null);

  const addHTSCode = () => {
    if (isEditingDetails && handleChange) {
      const newAdditionalHTSCodes = product?.additional_hts_codes
        ? [...product?.additional_hts_codes]
        : [];
      newAdditionalHTSCodes.push({ hts_code: '', country_of_origin: '' });
      handleChange('additional_hts_codes', newAdditionalHTSCodes);
    }
  };

  const deleteHTSCode = (index: number) => {
    if (isEditingDetails && handleChange) {
      const newAdditionalHTSCodes = product?.additional_hts_codes
        ? [...product?.additional_hts_codes]
        : [];
      handleChange(
        'additional_hts_codes',
        newAdditionalHTSCodes.filter((_, idx) => idx !== index),
      );
    }
  };

  const handleAdditionalHTSCodeChanges = (
    field: string,
    value: any,
    index: number,
  ) => {
    if (isEditingDetails && handleChange) {
      const newAdditionalHTSCodes = product?.additional_hts_codes
        ? [...product?.additional_hts_codes]
        : [];
      const current = newAdditionalHTSCodes[index];
      current[field] = value;
      newAdditionalHTSCodes[index] = current;
      handleChange('additional_hts_codes', newAdditionalHTSCodes);
    }
  };

  const getAdditionalColumns = () => {
    if (
      product?.additional_columns &&
      typeof product?.additional_columns === 'object' &&
      !Array.isArray(product?.additional_columns)
    ) {
      return product?.additional_columns;
    }

    return {};
  };

  const addAdditionalColumn = () => {
    if (!isEditingDetails || !handleChange) {
      return;
    }

    const additionalColumns = { ...getAdditionalColumns() };
    let newKeyIndex = 1;
    let newKey = `new_column_${newKeyIndex}`;

    while (Object.prototype.hasOwnProperty.call(additionalColumns, newKey)) {
      newKeyIndex += 1;
      newKey = `new_column_${newKeyIndex}`;
    }

    additionalColumns[newKey] = '';
    handleChange('additional_columns', additionalColumns);
  };

  const deleteAdditionalColumn = (index: number) => {
    if (!isEditingDetails || !handleChange) {
      return;
    }

    const additionalColumns = Object.entries(getAdditionalColumns()).filter(
      (_, idx) => idx !== index,
    );

    handleChange('additional_columns', Object.fromEntries(additionalColumns));
  };

  const handleAdditionalColumnChange = (
    index: number,
    field: 'key' | 'value',
    value: string,
  ) => {
    if (!isEditingDetails || !handleChange) {
      return;
    }

    const additionalColumns = Object.entries(getAdditionalColumns());
    const current = additionalColumns[index];

    if (!current) {
      return;
    }

    const [currentKey, currentValue] = current;
    additionalColumns[index] =
      field === 'key' ? [value, currentValue] : [currentKey, value];

    handleChange('additional_columns', Object.fromEntries(additionalColumns));
  };

  const handleDownloadTicket = async () => {
    const fileName = `${(product?.name || 'product')
      .toString()
      .trim()
      .replace(/\s+/g, '-')}`;

    try {
      setIsDownloadingPdf(true);
      await productTicketPDF(
        downloadRef,
        `${fileName}-${moment().format('YYYY-MM-DD-hh-mm-ss')}.pdf`,
        product?.name,
        {
          theme: 'light',
          quality: 100000,
          aggressiveCompression: false,
        },
      );
      enqueueSnackbar('Tariff ticket downloaded', { variant: 'success' });
    } catch (error) {
      console.error('Failed to download tariff ticket PDF:', error);
      enqueueSnackbar('Unable to download tariff ticket', { variant: 'error' });
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const [localSearchTermDocs] = useState('');
  const [sortByDocs, setSortByDocs] = useState<ColumnKeys>('created_at');
  const [sortOrderDocs, setSortOrderDocs] = useState<'asc' | 'desc'>('desc');
  const [currentPageDocs, setCurrentPageDocs] = useState(1);
  const [paginationDocs, setPaginationDocs] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 5,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [uploads, setUploads] = useState<any[]>([]);

  const fetchUploads = async () => {
    setIsLoading(true);
    const response = await FileAPI.getUploadsByDatabaseProductId(
      product?.database_product_id,
      {
        page: currentPageDocs,
        limit: 5,
        sortBy: sortByDocs,
        sortOrder: sortOrderDocs,
        search: localSearchTermDocs,
      },
    );
    if (response?.success && Array.isArray(response.result.data)) {
      setUploads(response.result.data);
      setPaginationDocs(response.result.pagination);
    } else {
      setUploads([]);
      setPaginationDocs({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 5,
        hasNextPage: false,
        hasPreviousPage: false,
      });
    }
    setIsLoading(false);
  };

  const [isFileUploadOpen, setIsFileUploadOpen] = useState(false);
  const handleCloseFileUpload = () => {
    setIsFileUploadOpen(false);
    fetchUploads();
  };

  const handleSortDocs = (key: ColumnKeys) => {
    const nextSortOrder =
      sortByDocs === key && sortOrderDocs === 'asc' ? 'desc' : 'asc';
    setSortByDocs(key);
    setSortOrderDocs(nextSortOrder);
    fetchUploads();
  };

  const handlePageChangeDocs = (_e: ChangeEvent<unknown>, newPage: number) => {
    setCurrentPageDocs(newPage);
    fetchUploads();
  };

  // Comment-related state
  const [commentContent, setCommentContent] = useState('');

  // Audit log state
  const [currentAuditPage, setCurrentAuditPage] = useState(1);

  // React Query hooks for comments
  const {
    data: comments = [],
    isLoading: isLoadingComments,
    error: commentsError,
  } = useGetCommentsByCommentableId(
    supplier?.association_id,
    'DatabaseProduct',
  );

  // Comment mutation hooks
  const createCommentMutation = useCreateComment();
  const deleteCommentMutation = useDeleteComment();

  // Status update mutation hooks
  const updateProductStatusMutation = useUpdateProductStatus();
  const updateProductMutation = useUpdateProduct();

  const productPagination: PaginationParams = {
    page: 1,
    limit: 500,
    sortBy: 'name',
    sortOrder: 'asc',
  };

  const { data: productsData, error: productsError } =
    supplier?.database_structure === 'hts-code-grouping'
      ? useGetProductsByHTSCode(supplier?.id, '', productPagination)
      : useGetProductsByProductId(supplier?.id, '', productPagination);

  // Handle products loading errors
  useEffect(() => {
    if (productsError) {
      enqueueSnackbar('Failed to fetch products', { variant: 'error' });
    }
  }, [productsError]);

  // Handle products loading data
  useEffect(() => {
    const countryCountsUpdated = productsData.reduce((acc, prod) => {
      const country = prod.country_of_origin ?? 'Unknown';
      acc[country] = (acc[country] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    setCountryCounts(countryCountsUpdated);

    const countryValuesUpdated = productsData.reduce((acc, prod) => {
      const country = prod.country_of_origin ?? 'Unknown';
      const value = Number(prod.tariff_result?.math?.totalCost) || 0;
      acc[country] = (acc[country] || 0) + value;
      return acc;
    }, {} as Record<string, number>);
    setCountryValues(countryValuesUpdated);
    fetchUploads();
  }, []);

  const {
    data: screeningHistoryData,
    isLoading: isScreeningHistoryLoading,
    error: screeningHistoryError,
  } = useGetScreeningHistoryByProductId(
    supplier?.association_id || supplier?.database_product_id || supplier?.id,
  );

  const formatScreenDate = (isoString?: string | null) => {
    if (!isoString) {
      return '-';
    }
    const parsed = new Date(isoString);
    if (Number.isNaN(parsed.getTime())) {
      return '-';
    }
    return format(parsed, 'MMM d, yyyy h:mm a');
  };

  const toggleProductDocVisibilityMutation = useToggleProductDocVisibility();

  const auditProductId = supplier?.id;

  const {
    data: productAuditLogsData = [],
    isLoading: isLoadingProductAuditLogs,
    error: productAuditLogsError,
  } = useGetAuditLogsByProductId(
    auditProductId || '',
    AUDIT_ITEMS_PER_PAGE,
    (currentAuditPage - 1) * AUDIT_ITEMS_PER_PAGE,
  );

  // Fallback: if no entity/product specific logs, try database-level logs
  const {
    data: databaseAuditLogsData = [],
    isLoading: isLoadingDatabaseAuditLogs,
    error: databaseAuditLogsError,
  } = useGetAuditLogsByDatabaseId(
    supplier?.database_id || '',
    AUDIT_ITEMS_PER_PAGE,
    (currentAuditPage - 1) * AUDIT_ITEMS_PER_PAGE,
    true, // includeErrors: show failure audit logs alongside normal activity
  );

  const [shareModalOpen, setShareShowModalOpen] = useState<boolean>(false);
  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] =
    useState<boolean>(false);
  const [isChangingStatus, setIsChangingStatus] = useState<boolean>(false);
  const [currentStatusFlag, setCurrentStatusFlag] = useState<string>(
    supplier?.status_flag || 'new',
  );

  const [isAuditModalOpen, setIsAuditModalOpen] = useState<boolean>(false);
  const [isEditTariffModalOpen, setIsEditTariffModalOpen] =
    useState<boolean>(false);
  const [isTariffHistoryOpen, setIsTariffHistoryOpen] = useState<boolean>(false);

  // Use entity/product specific logs if available, otherwise fall back to database logs
  const currentAuditLogsData =
    productAuditLogsData.length > 0
      ? productAuditLogsData
      : databaseAuditLogsData;
  const isLoadingCurrentAuditLogs =
    isLoadingProductAuditLogs || isLoadingDatabaseAuditLogs;
  const currentAuditLogsError = productAuditLogsError || databaseAuditLogsError;

  // Debug logging for audit data
  useEffect(() => {
    if (currentAuditLogsError) {
      // eslint-disable-next-line no-console
      console.error('Audit Debug - Error:', currentAuditLogsError);
    }
  }, [currentAuditLogsData, isLoadingCurrentAuditLogs, currentAuditLogsError]);

  const queryClient = useQueryClient();
  const { data: screeningStatus, refetch: refetchScreeningStatus } =
    useGetDatabaseScreeningStatus(supplier?.database_id);

  const isScreeningCurrently = screeningStatus?.inProgress || false;
  const screeningProgress = screeningStatus?.progress || null;

  useEffect(() => {
    if (!screeningStatus?.inProgress) {
      getProductById(supplier?.id).then((prod) => setProduct(prod));
    }
  }, [supplier, screeningStatus?.inProgress]);

  // Track screening status to detect completion
  const prevInProgressRef = useRef(false);

  useEffect(() => {
    if (prevInProgressRef.current && !isScreeningCurrently) {
      // Screening just finished! Invalidate queries to refresh data
      const id =
        supplier?.association_id ||
        supplier?.database_entity_id ||
        supplier?.id;

      if (id) {
        queryClient.invalidateQueries({
          queryKey: screeningKeys.entityHistoryById(String(id)),
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['audit'],
      });
      // Also refresh the supplier itself if needed, or other related data
    }
    prevInProgressRef.current = isScreeningCurrently;
  }, [isScreeningCurrently, queryClient, supplier]);

  // Detect when saving finishes to check for new screening status
  const prevIsSavingRef = useRef(false);
  useEffect(() => {
    if (prevIsSavingRef.current && !isSavingDetails) {
      // Just finished saving details, check for screening status
      refetchScreeningStatus();
    }
    prevIsSavingRef.current = isSavingDetails;
  }, [isSavingDetails, refetchScreeningStatus]);

  type CreateCommentInput = {
    commentable_type: 'DatabaseEntity' | 'DatabaseProduct';
    commentable_id: string;
    content: string;
    author_id?: string;
    author_name?: string;
    parent_comment_id?: string | null;
  };

  // Sync local status with supplier prop changes
  useEffect(() => {
    setCurrentStatusFlag(supplier?.status_flag || 'new');
  }, [supplier?.status_flag]);

  useEffect(() => {
    setIsEditingDetails(false);
    setIsSavingDetails(false);
  }, [supplier?.id]);

  const decompressData = (compressedData: any) => {
    if (compressedData === undefined || compressedData.length === 0) {
      return {};
    }
    const compressedBuffer = Buffer.from(compressedData, 'base64');
    const decompressed = pako.ungzip(compressedBuffer, { to: 'string' });
    return JSON.parse(decompressed);
  };

  useEffect(() => {
    if (supplier && Array.isArray(supplier?.hidden_ids)) {
      const hiddenIds = supplier?.hidden_ids.map((id: any) => String(id));
      setRemovedDocIds(hiddenIds);
    } else {
      setRemovedDocIds([]);
    }
  }, [supplier]);

  // Handle comment loading errors
  useEffect(() => {
    if (commentsError) {
      enqueueSnackbar('Failed to load comments', { variant: 'error' });
    }
  }, [commentsError]);

  const handleAddComment = async () => {
    if (!commentContent.trim() || !supplier?.id || !cur_user) {
      return;
    }

    try {
      const commentData: CreateCommentInput = {
        commentable_type: 'DatabaseProduct',
        commentable_id: String(supplier?.association_id ?? ''),
        content: commentContent.trim(),
        author_id: cur_user.basics?.id ?? undefined,
        author_name:
          (cur_user.basics?.firstName && cur_user.basics?.lastName
            ? `${cur_user.basics.firstName} ${cur_user.basics.lastName}`
            : cur_user.basics?.firstName
            ? `${cur_user.basics.firstName}`
            : cur_user.basics?.email) || 'Anonymous',
      };

      const result = await createCommentMutation.mutateAsync(commentData);

      if (result) {
        setCommentContent('');
        enqueueSnackbar('Comment added successfully', { variant: 'success' });
        // React Query should refetch via your hook’s onSuccess
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      enqueueSnackbar('Error adding comment', { variant: 'error' });
    }
  };

  const handleDeleteComment = async (commentId?: string) => {
    if (!commentId) {
      enqueueSnackbar('Unable to delete comment. Please try again later', {
        variant: 'error',
      });
      return;
    }
    try {
      const result = await deleteCommentMutation.mutateAsync(commentId);
      if (result) {
        enqueueSnackbar('Comment deleted successfully', { variant: 'success' });
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      enqueueSnackbar('Error deleting comment', { variant: 'error' });
    }
  };

  const handleStatusUpdate = async (status: string) => {
    try {
      setIsChangingStatus(true);

      if (supplier?.type === 'products') {
        if (!supplier?.id) {
          enqueueSnackbar('Product ID is missing', { variant: 'error' });
          setIsChangingStatus(false);
          return;
        }

        const productData = { status_flag: status };
        const result = await updateProductStatusMutation.mutateAsync({
          id: supplier?.id,
          statusData: productData,
        });

        if (result) {
          enqueueSnackbar('Product status updated successfully', {
            variant: 'success',
          });
          setIsChangeStatusModalOpen(false);
          setCurrentStatusFlag(status);
          setTrigger(!trigger);
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
      enqueueSnackbar('Error updating status', { variant: 'error' });
    } finally {
      setIsChangingStatus(false);
    }
  };
  const [deniedPartyHits, setDeniedPartyHits] = useState({ docs: [] });
  // Process screening history data when it changes - using new endpoint structure
  useEffect(() => {
    if (screeningHistoryData?.latestResult) {
      const latestResult = screeningHistoryData.latestResult;
      setDeniedPartyHits(latestResult.denied_party_results || {});
      // Get all_results from the latest screening result
      if (latestResult.all_results) {
        try {
          // all_results is a string that may be compressed or JSON
          // eslint-disable-next-line
          let parsedData: any;

          // Try to decompress if it's base64 compressed data
          try {
            const decompData = decompressData(latestResult.all_results);
            parsedData =
              typeof decompData === 'string'
                ? JSON.parse(decompData)
                : decompData;
          } catch (decompError) {
            // If decompression fails, try parsing as regular JSON
            parsedData = JSON.parse(latestResult.all_results);
          }

          // Handle the case where parsedData might not be an array
          const dataArray = Array.isArray(parsedData)
            ? parsedData
            : [parsedData];

          // Structure the data as expected by LensesTable
          const processedData = [
            {
              field: supplier?.name,
              hsCode: supplier?.hts_code,
              docs: markAndMoveRemoved(dataArray),
              hits: latestResult.hit_count || dataArray.length,
              new_total: latestResult.new_total,
              diff_added_count: latestResult.diff_added_count,
              diff_removed_count: latestResult.diff_removed_count,
              diff_modified_count: latestResult.diff_modified_count,
              is_base_snapshot: latestResult.is_base_snapshot,
              ai_summary: latestResult.ai_summary,
              doc_info: latestResult.doc_info,
              screening_timestamp: latestResult.screening_timestamp,
            },
          ];

          setPassedDownResults(processedData);
        } catch (error) {
          console.error('Error parsing screening results:', error);
          enqueueSnackbar('Error parsing screening results', {
            variant: 'error',
          });
          setPassedDownResults([]);
        }
      } else {
        setPassedDownResults([]);
      }
    } else if (screeningHistoryData === null) {
      // No screening history found
      setPassedDownResults([]);
    }
  }, [screeningHistoryData, removedDocIds, supplier?.name]);

  // Handle screening history loading errors
  useEffect(() => {
    if (screeningHistoryError) {
      enqueueSnackbar('Failed to fetch screening history', {
        variant: 'error',
      });
    }
  }, [screeningHistoryError]);

  // Handle audit logs loading errors
  useEffect(() => {
    if (currentAuditLogsError) {
      enqueueSnackbar('Failed to fetch audit logs', {
        variant: 'error',
      });
    }
  }, [currentAuditLogsError]);

  // Update loading state based on React Query loading state
  useEffect(() => {
    if (isScreeningHistoryLoading !== isLoading) {
      setIsLoading(isScreeningHistoryLoading);
    }
  }, [isScreeningHistoryLoading]);

  const [passedDownResults, setPassedDownResults] = useState<any[]>([]);
  //Lenses fields: may need to clean up
  const [selectedItems, setSelectedItems] = useState<any[]>([]);

  const [activeFilter] = useState('BITE Lens');
  const [curLevel, setCurLevel] = useState(2);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentItems, setCurrentItems] = useState<any[]>([]);

  const aiSums = useMemo(() => {
    const ai = screeningHistoryData?.latestResult?.ai_summary;
    if (!ai) {
      return [
        {
          entity_name: supplier?.name || '-',
          risk: '-',
          best_match: '-',
          match_probability: 0,
          explanation: '-',
          screenDate: '-',
          document_count: 0,
          top_matches: [],
        },
      ];
    }
    // Prefer normalized `top_matches`, else adapt from `best_matches`
    const legacyBestMatches = Array.isArray((ai as any)?.best_matches)
      ? (ai as any).best_matches
      : null;

    const topMatchesArray = Array.isArray(ai.top_matches)
      ? ai.top_matches
      : legacyBestMatches
      ? legacyBestMatches.map((m: any) => ({
          id: m.id ?? null,
          name: m.name,
          confidence: m.confidence,
        }))
      : [];

    const top0 = topMatchesArray[0];
    const best_match = ai.best_match ?? (top0?.name || '-');

    const match_probability =
      typeof ai.match_probability === 'number'
        ? ai.match_probability
        : typeof top0?.confidence === 'number'
        ? top0.confidence
        : 0;

    return [
      {
        entity_name: ai.entity_name || supplier?.name || '-',
        risk: ai.risk ?? '-',
        best_match: best_match,
        match_probability: match_probability,
        explanation: ai.explanation ?? '-',
        document_count: (ai as any)?.document_count ?? 0,
        screenDate: formatScreenDate(
          screeningHistoryData?.latestResult?.created_at,
        ),
        // osint is now on-demand inside AIEvalEntity, so it’s fine if this is missing
        osint: undefined,
        top_matches: topMatchesArray,
      },
    ];
  }, [supplier, screeningHistoryData]);

  const tariffFullResults =
    screeningHistoryData?.latestTariffResult?.product_result;
  const inputFieldSx = {
    height: '45px',
    width: '100%',
    '& .MuiInputBase-root': {
      backgroundColor: 'var(--input-color)',
      borderRadius: '8px',
      height: '45px',
      paddingRight: '8px',
      color: 'var(--input-text-color)',
      display: 'flex',
      alignSelf: 'stretch',
      border:
        '1px solid var(--Colors-Border-border-primary, var(--bg-border-db))',
      background: 'var(--input-color)',
      boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
      fontFamily: 'Inter',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '500',
      lineHeight: '20px',
    },
    '& input': {
      color: 'var(--input-text-color)',
      fontSize: '14px',
      fontWeight: 500,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--secondary-color)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--input-placeholder-color)',
    },
    '& .MuiOutlinedInput-root.Mui-error:hover fieldset': {
      borderColor: 'var(--input-placeholder-color)',
    },
    '& .MuiOutlinedInput-root.Mui-focused.Mui-error fieldset': {
      borderColor: 'var(--input-placeholder-color)',
    },
    '& .MuiFormLabel-root': {
      color: 'var(--focused-text-color)',
      fontSize: '14px',
    },
    '& .MuiSvgIcon-root': {
      color: 'var(--focused-text-color)',
    },
  };

  const textAreaSx = {
    width: '100%',
    '& .MuiInputBase-root': {
      backgroundColor: 'var(--input-color)',
      borderRadius: '8px',
      paddingRight: '8px',
      color: 'var(--input-text-color)',
      display: 'flex',
      alignSelf: 'stretch',
      border:
        '1px solid var(--Colors-Border-border-primary, var(--bg-border-db))',
      background: 'var(--input-color)',
      boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
      fontFamily: 'Inter',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '500',
      lineHeight: '20px',
    },
    '& input': {
      color: 'var(--input-text-color)',
      fontSize: '14px',
      fontWeight: 500,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--secondary-color)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--input-placeholder-color)',
    },
    '& .MuiOutlinedInput-root.Mui-error:hover fieldset': {
      borderColor: 'var(--input-placeholder-color)',
    },
    '& .MuiOutlinedInput-root.Mui-focused.Mui-error fieldset': {
      borderColor: 'var(--input-placeholder-color)',
    },
    '& .MuiFormLabel-root': {
      color: 'var(--focused-text-color)',
      fontSize: '14px',
    },
    '& .MuiSvgIcon-root': {
      color: 'var(--focused-text-color)',
    },
  };

  const CustomSelect = styled(Select)({
    '.MuiSelect-icon': {
      color: 'var(--card-text)',
    },
    height: '45px',
    '& .MuiInputBase-root': {
      backgroundColor: 'var(--input-color)',
      borderRadius: '8px',
      height: '45px',
      paddingRight: '8px',
      color: 'var(--input-text-color)',
      display: 'flex',
      alignSelf: 'stretch',
      border:
        '1px solid var(--Colors-Border-border-primary, var(--bg-border-db))',
      background: 'var(--input-color)',
      boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
      fontFamily: 'Inter',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '500',
      lineHeight: '20px',
    },
    '& input': {
      color: 'var(--input-text-color)',
      fontSize: '14px',
      fontWeight: 500,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--secondary-color)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--input-placeholder-color)',
    },
    '& .MuiOutlinedInput-root.Mui-error:hover fieldset': {
      borderColor: 'var(--input-placeholder-color)',
    },
    '& .MuiOutlinedInput-root.Mui-focused.Mui-error fieldset': {
      borderColor: 'var(--input-placeholder-color)',
    },
    '& .MuiFormLabel-root': {
      color: 'var(--focused-text-color)',
      fontSize: '14px',
    },
    '& .MuiSvgIcon-root': {
      color: 'var(--focused-text-color)',
    },
  });

  const handleDetailsCancel = () => {
    setIsEditingDetails(false);
  };

  const handleDetailsSave = async () => {
    try {
      setIsSavingDetails(true);
      setIsEditingDetails(true);
      if (!supplier?.id) {
        enqueueSnackbar('Product ID is missing', { variant: 'error' });
        setIsEditingDetails(false);
        setIsEditTariffModalOpen(false);
        return;
      }

      const result = await updateProductMutation.mutateAsync({
        id: product?.id,
        productData: product,
      });

      if (result?.success) {
        enqueueSnackbar('Product updated successfully', {
          variant: 'success',
        });
        await runScreen(true);
        const updated = await getProductById(supplier?.id);
        setProduct(updated);
      } else {
        enqueueSnackbar(`Error updating product?. ${result?.message}`, {
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error updating product:', error);
      enqueueSnackbar('Error updating product', { variant: 'error' });
    } finally {
      setIsSavingDetails(false);
      setIsEditingDetails(false);
      setIsEditTariffModalOpen(false);
    }
  };

  const removeHit = async (id: string, name: string, item?: any) => {
    try {
      const result = await toggleProductDocVisibilityMutation.mutateAsync({
        parentId: supplier?.association_id,
        itemId: id,
      });

      if (result?.success) {
        setRemovedDocIds((prev) => {
          const docId = item?.['BITE Record ID'];
          const isCurrentlyRemoved = prev.includes(docId);

          if (isCurrentlyRemoved) {
            return prev.filter((removedId) => removedId !== docId);
          }
          return [...prev, docId];
        });

        enqueueSnackbar('Document visibility toggled', {
          variant: 'success',
        });

        // React Query will automatically invalidate and refetch the screening history
        // due to the onSuccess callback in the mutation hooks
      } else {
        enqueueSnackbar('Error toggling document visibility', {
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error in removeHit:', error);
      enqueueSnackbar('Error toggling document visibility', {
        variant: 'error',
      });
    }
  };

  /**
   * Mark removed items and move them to the bottom of the array
   * @param {any[]} arr - Array of items to process
   * @returns {any[]} Array with removed items marked and moved to bottom
   */
  const markAndMoveRemoved = (arr: any[]) => {
    const remaining: any[] = [];
    const removed: any[] = [];

    for (const item of arr) {
      const docId = item['BITE Record ID'] || item.id;
      if (removedDocIds.includes(docId)) {
        removed.push({ ...item, removed: true });
      } else {
        remaining.push(item);
      }
    }

    return [...remaining, ...removed];
  };

  // Extract screening date from React Query screening history data
  const screeningDate = useMemo(() => {
    // Get from new endpoint structure
    if (screeningHistoryData?.latestResult) {
      const latestResult = screeningHistoryData.latestResult;
      return (
        latestResult.last_screening_date ||
        latestResult.screening_timestamp ||
        latestResult.created_at
      );
    }

    // Fallback to supplier data if React Query data not available
    if (supplier?.screening && supplier?.screening.length > 0) {
      const latestScreening = supplier?.screening[0];
      return latestScreening.created_at || latestScreening.screening_timestamp;
    }

    return null;
  }, [screeningHistoryData, supplier]);

  const mockCase = {
    data: {
      removedHits: removedDocIds,
      screeningDate: screeningDate,
    },
  };

  // Transform audit log data to match AuditLogRow expected format
  const auditActivities = useMemo(() => {
    if (!currentAuditLogsData || !Array.isArray(currentAuditLogsData)) {
      return [];
    }

    return currentAuditLogsData.map((auditLog) => {
      const affectedItems = auditLog.affected_items || [];

      let itemMessage: string | undefined = undefined;
      let matchingItem = undefined;

      matchingItem = affectedItems.find(
        (item) =>
          item?.product_id && String(item.product_id) === auditProductId,
      );
      itemMessage = matchingItem?.message ?? undefined;

      const resolvedMessage =
        itemMessage || auditLog.message || 'No action specified';

      return {
        user: {
          firstName: auditLog.user_name?.split(' ')[0] || 'Unknown',
          lastName: auditLog.user_name?.split(' ').slice(1).join(' ') || 'User',
        },
        action: resolvedMessage,
        timeStamp: auditLog.updated_at || new Date().toISOString(),
      };
    });
  }, [currentAuditLogsData, auditProductId, supplier?.type]);

  // For now, we'll use the current page data length to estimate total pages
  // In a real implementation, you'd want the API to return total count
  const totalAuditPages = Math.max(
    1,
    currentAuditPage +
      (auditActivities.length === AUDIT_ITEMS_PER_PAGE ? 1 : 0),
  );

  const handleNextAuditPage = () => {
    setCurrentAuditPage((prevPage) => Math.min(prevPage + 1, totalAuditPages));
  };

  const handlePrevAuditPage = () => {
    setCurrentAuditPage((prevPage) => Math.max(prevPage - 1, 1));
  };

  const currentAuditItems = auditActivities;

  const runScreen = async (skipEmail?: boolean) => {
    if (!supplier?.database_id) {
      enqueueSnackbar('Database ID not found', {
        variant: 'error',
      });
      return;
    }

    const payload = {
      databaseId: supplier?.database_id,
      databaseProductId:
        supplier?.association_id ?? supplier?.database_product_id ?? undefined,
      productId: supplier?.id ?? undefined,
      initiatedBy: cur_user?.basics?.id,
      skipEmail: skipEmail,
    };

    enqueueSnackbar('Starting screening...', { variant: 'info' });
    if (payload.databaseProductId || payload.productId) {
      runProductScreeningMutation.mutate(payload, {
        onSuccess: (response) => {
          const successes = Array.isArray(response?.result?.results)
            ? response.result.results.filter((r: any) => r.success)
            : null;
          const hasSuccess = successes ? successes.length > 0 : true;

          enqueueSnackbar(
            hasSuccess
              ? 'Screening started successfully'
              : 'Screening request accepted with warnings',
            { variant: hasSuccess ? 'success' : 'warning' },
          );
        },
        onError: (error: any) => {
          enqueueSnackbar(
            error?.response?.data?.message || 'Failed to start screening',
            { variant: 'error' },
          );
        },
      });
    } else {
      runScreeningMutation.mutate(
        { databaseIds: [supplier?.database_id] },
        {
          onSuccess: () => {
            enqueueSnackbar('Screening started successfully', {
              variant: 'success',
            });
          },
          onError: (error: any) => {
            enqueueSnackbar(
              error?.response?.data?.message || 'Failed to start screening',
              { variant: 'error' },
            );
          },
        },
      );
    }
  };
  const [crossLinks, setCrossLinks] = useState<string[]>([]);
  const [crossLoading, setCrossLoading] = useState(false);
  const [isExpandedCross, setIsExpandedCross] = useState(false);
  const [isExpandedDenied, setIsExpandedDenied] = useState(false);

  const fetchCrossRulings = async (): Promise<void> => {
    try {
      setCrossLinks(product?.cross_rulings);
    } catch (error) {
      console.error('Error fetching cross rulings:', error);
      enqueueSnackbar('Failed to fetch cross rulings', { variant: 'error' });
      return;
    }
    return;
  };

  // On demand: CROSS rulings are no longer computed during bulk screening (that was
  // an AI call per product). Fetch them when the user asks; the server caches the
  // result to DatabaseProduct.cross_rulings.
  const loadCrossRulings = async (): Promise<void> => {
    setCrossLoading(true);
    try {
      const links = await fetchProductCrossRulings({
        databaseProductId:
          supplier?.association_id || supplier?.database_product_id || supplier?.id,
        hts_code: product?.hts_code || supplier?.hts_code,
        product_description: product?.description || product?.name || supplier?.name,
      });
      setCrossLinks(Array.isArray(links) ? links : []);
    } catch (error) {
      enqueueSnackbar('Failed to fetch CROSS rulings', { variant: 'error' });
    } finally {
      setCrossLoading(false);
    }
  };

  useEffect(() => {
    fetchCrossRulings();
  }, [product?.cross_rulings]);

  return (
    <div className='team-content-container' key='edit-product'>
      <div className='modal-content-header'>
        <div className='modal-content-title' style={{ width: '100%' }}>
          <Inventory2OutlinedIcon
            sx={{
              fontSize: 20,
              color: 'var(--Primary-400, var(--action-color))',
            }}
          />{' '}
          {supplier && supplier?.name}
          <span
            className={supplier?.hierarchy_type}
            style={{ marginLeft: '10px' }}
          >
            {supplier && supplier?.hierarchy_type}
          </span>
          <div className='module-header-btns' style={{ float: 'right' }}>
            <div className='btn-new-automate' onClick={() => runScreen()}>
              <div className='modal-btn-text'>
                {runScreeningMutation.isPending ||
                runProductScreeningMutation.isPending
                  ? 'Running...'
                  : 'Run Screen'}
              </div>
            </div>
            <div
              className='btn-new-automate'
              onClick={() => setIsAuditModalOpen(true)}
            >
              <div className='modal-btn-text'>Audit Log</div>
            </div>
            <button
              type='button'
              className='btn-new-automate'
              onClick={handleDownloadTicket}
              disabled={isDownloadingPdf}
            >
              <div className='modal-btn-text'>
                {isDownloadingPdf ? 'Downloading...' : 'Download Ticket'}
              </div>
            </button>
            <button
              type='button'
              className='btn-new-automate'
              onClick={() => setIsFileUploadOpen(true)}
            >
              <div className='modal-btn-text'>Upload Documents</div>
            </button>
            <div className='modal-delete-btn' onClick={() => history.goBack()}>
              <div className='modal-btn-text'>{'X Close'}</div>
            </div>
          </div>
        </div>
      </div>
      <div ref={downloadRef} style={{ width: '100%' }}>
        <div className='results-top-grid'>
          <div className='results-card'>
            <div className='results-card-header'>
              <Balance
                sx={{
                  color: 'var(--action-color)',
                  width: '1em',
                  height: '1em',
                }}
              />{' '}
              Compliance Score
            </div>
            <BarGraph
              showValueLabel={true}
              currentValue={product?.compliance_score}
              totalValue={100}
              color={
                product?.compliance_score >= 90
                  ? 'var(--full-bar-color)'
                  : product?.compliance_score >= 60
                  ? 'var(--mid-color)'
                  : 'var(--low-color)'
              }
              status={
                product?.compliance_score >= 90
                  ? 'Good'
                  : product?.compliance_score >= 60
                  ? 'Medium'
                  : 'Low'
              }
            />
            <button
              type='button'
              className='results-link-btn'
              onClick={() => setOpenRules(!openRules)}
            >
              How We Calculate Your Score
            </button>
            <ul className='flag-list'>
              {product?.issues &&
                product?.issues
                  .filter(
                    (iss: string) =>
                      SCORE_DEDUCTIONS.hasOwnProperty(iss) &&
                      !(
                        iss === 'Invalid HTS Code' &&
                        product?.issues?.includes('Missing HTS Code')
                      ),
                  )
                  .map((iss: string) => {
                    return (
                      <li key={iss} className='flag-pill flag-pill--deduction'>
                        <span className='flag-pill__label'>{iss}</span>
                        <span className='flag-pill__value'>
                          {fetchDeduction(iss)}
                        </span>
                        <input
                          type='checkbox'
                          className='flag-pill__check'
                          checked={
                            !product?.clear_flags ||
                            !product?.clear_flags.includes(iss)
                          }
                          onChange={(e) => handleClearChange(e, iss)}
                          title='Uncheck to clear this deduction'
                        />
                      </li>
                    );
                  })}
            </ul>
          </div>

          <div className='results-card'>
            <div className='results-card-header'>
              <OutlinedFlagIcon
                sx={{
                  color: 'var(--action-color)',
                  width: '1em',
                  height: '1em',
                }}
              />
              <span>Flags</span>
            </div>

            <ul className='flag-list'>
              {(!product?.issues || product?.issues?.length === 0) && (
                <li className='flag-empty'>No flags</li>
              )}
              {product?.issues
                ?.filter(
                  (iss: string) =>
                    !(
                      iss === 'Invalid HTS Code' &&
                      product?.issues?.includes('Missing HTS Code')
                    ) &&
                    !(
                      iss === 'Additional Duty Rate > 0%' &&
                      product?.issues?.includes('Duty Cost > 0')
                    ),
                )
                .map((flag: string) => (
                  <li key={flag} className='flag-pill flag-pill--warn'>
                    <span className='flag-pill__label'>{flag}</span>
                    <span className='flag-pill__value flag-pill__value--warn'>
                      ⚠
                    </span>
                  </li>
                ))}
            </ul>
          </div>

          <div className='results-card'>
            <div className='results-card-header'>
              <AutoAwesomeIcon
                sx={{
                  marginRight: '10px',
                  color: 'var(--action-color)',
                  width: '1em',
                  height: '1em',
                }}
              />
              AI Summary
            </div>
            <p className='results-ai-summary'>
              {aiSums && aiSums.length > 0 && aiSums[0].explanation}
            </p>
          </div>
          <div className='results-card cols-span-three'>
            <div className='results-card-header'>
              <svg
                width='22'
                height='22'
                viewBox='0 0 20 20'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M10.5 9H15M10.5 12H15M10.5 15H15M3 3.75C3 3.55109 3.07902 3.36032 3.21967 3.21967C3.36032 3.07902 3.55109 3 3.75 3H14.25C14.4489 3 14.6397 3.07902 14.7803 3.21967C14.921 3.36032 15 3.55109 15 3.75V5.25C15 5.44891 14.921 5.63968 14.7803 5.78033C14.6397 5.92098 14.4489 6 14.25 6H3.75C3.55109 6 3.36032 5.92098 3.21967 5.78033C3.07902 5.63968 3 5.44891 3 5.25V3.75ZM3 9.75C3 9.55109 3.07902 9.36032 3.21967 9.21967C3.36032 9.07902 3.55109 9 3.75 9H6.75C6.94891 9 7.13968 9.07902 7.28033 9.21967C7.42098 9.36032 7.5 9.55109 7.5 9.75V14.25C7.5 14.4489 7.42098 14.6397 7.28033 14.7803C7.13968 14.921 6.94891 15 6.75 15H3.75C3.55109 15 3.36032 14.921 3.21967 14.7803C3.07902 14.6397 3 14.4489 3 14.25V9.75Z'
                  stroke='var(--action-color)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              Product Details
              <div className='module-header-btns' style={{ float: 'right' }}>
                {isEditingDetails && (
                  <>
                    <button
                      type='button'
                      className='btn-new-automate'
                      onClick={handleDetailsSave}
                      disabled={isSavingDetails}
                    >
                      {isSavingDetails ? (
                        <>
                          <CircularProgress
                            size={16}
                            color='inherit'
                            style={{ marginRight: '8px' }}
                          />
                          <div className='modal-btn-text'>Saving...</div>
                        </>
                      ) : (
                        <div className='modal-btn-text'>Save Changes</div>
                      )}
                    </button>
                    <button
                      type='button'
                      className='btn-new-automate'
                      onClick={handleDetailsCancel}
                      disabled={
                        isSavingDetails ||
                        isCheckingSubscription ||
                        !userHasMonitor
                      }
                    >
                      <div className='modal-btn-text'>Cancel</div>
                    </button>
                  </>
                )}
                {!isEditingDetails && (
                  <button
                    type='button'
                    className='btn-new-automate'
                    onClick={() => setIsEditingDetails(true)}
                    disabled={
                      isSavingDetails ||
                      isCheckingSubscription ||
                      !userHasMonitor
                    }
                  >
                    <div className='modal-btn-text'>Edit</div>
                  </button>
                )}
              </div>
            </div>

            <div className='export-divider cols-span-four'></div>

            <div
              className='widget-content cols-four'
              style={{ display: 'grid' }}
            >
              <div className='edit-form-item cols-span-one'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  Name:
                </span>
                {isEditingDetails ? (
                  <TextField
                    className='screening-input-field-content'
                    sx={inputFieldSx}
                    value={product?.name || ''}
                    onChange={(e: any) =>
                      handleChange && handleChange('name', e.target.value)
                    }
                  />
                ) : (
                  <span>{product?.name || 'N/A'}</span>
                )}
              </div>

              <div className='edit-form-item cols-span-one'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  Part Number/SKU:
                </span>
                {isEditingDetails ? (
                  <TextField
                    className='screening-input-field-content'
                    sx={inputFieldSx}
                    value={product?.model_number || ''}
                    onChange={(e: any) =>
                      handleChange &&
                      handleChange('model_number', e.target.value)
                    }
                  />
                ) : (
                  <span>{product?.model_number || 'N/A'}</span>
                )}
              </div>

              <div className='edit-form-item cols-span-one'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  HTS Code:
                </span>
                {isEditingDetails ? (
                  <Autocomplete
                    freeSolo
                    options={htsOptions}
                    loading={isHtsLoading}
                    // Format the selected code with dots inside the input box
                    getOptionLabel={(option) =>
                      typeof option === 'string'
                        ? option
                        : formatHtsPretty(option.code)
                    }
                    renderOption={(props, option) => (
                      <li {...props} key={option.code}>
                        {option.label}
                      </li>
                    )}
                    filterOptions={filterHtsOptions}
                    value={
                      product?.hts_code
                        ? htsOptions.find(
                            (o) =>
                              o.code === product?.hts_code.replace(/\D/g, ''),
                          ) || {
                            code: product?.hts_code.replace(/\D/g, ''),
                            label: product?.hts_code,
                          }
                        : null
                    }
                    isOptionEqualToValue={(option, val) =>
                      option.code === val.code
                    }
                    inputValue={product?.hts_code}
                    //eslint-disable-next-line
                    onInputChange={async (event, newInputValue, reason) => {
                      // 1. Fully wipe everything on clear
                      if (reason === 'clear') {
                        handleChange('hts_code', '');
                        setHtsOptions([]);
                        setFetchedPrefix('');
                        return;
                      }

                      handleChange('hts_code', newInputValue);

                      // 2. THIS FIXES THE RESET BUG:
                      // Keep the actual form state in sync with backspaces/typing
                      if (reason === 'input') {
                        handleChange(
                          'hts_code',
                          newInputValue.replace(/\D/g, ''),
                        );
                      }

                      const digits = newInputValue.replace(/\D/g, '');

                      if (digits.length >= 6) {
                        const prefix = digits.slice(0, 6);

                        if (prefix !== fetchedPrefix) {
                          setFetchedPrefix(prefix);
                          setIsHtsLoading(true);
                          try {
                            const formattedPrefix = formatHtsPretty(prefix);
                            const res = await DatahubAPI.retrieveHTSCode({
                              code: formattedPrefix,
                              country: product?.country_of_origin || '',
                            });

                            const hits =
                              res?.result?.result?.body?.hits?.hits || [];
                            const optionsMap = new Map();

                            hits.forEach((hit: any) => {
                              const src = hit._source;
                              if (src && src['HS Code']) {
                                const rawCode = String(src['HS Code'])
                                  .split(' - ')[0]
                                  .trim();
                                const d = rawCode.replace(/\D/g, '');

                                if (d.length >= 10) {
                                  const code10 = d.slice(0, 10);
                                  if (!optionsMap.has(code10)) {
                                    const pretty = formatHtsPretty(code10);
                                    const desc = src['Product Name'] || '';
                                    optionsMap.set(code10, {
                                      code: code10,
                                      label: `${pretty} - ${desc}`,
                                    });
                                  }
                                }
                              }
                            });
                            setHtsOptions(Array.from(optionsMap.values()));
                          } catch (e) {
                            console.error(
                              'Error fetching HTS prefix options',
                              e,
                            );
                          } finally {
                            setIsHtsLoading(false);
                          }
                        }
                      } else if (digits.length < 6) {
                        setHtsOptions([]);
                        setFetchedPrefix('');
                      }
                    }}
                    //eslint-disable-next-ine
                    onChange={(event, newValue) => {
                      if (typeof newValue === 'string') {
                        handleChange('hts_code', newValue.replace(/\D/g, ''));
                      } else if (newValue && newValue.code) {
                        handleChange('hts_code', newValue.code);
                      } else {
                        handleChange('hts_code', '');
                      }
                    }}
                    componentsProps={{
                      popper: {
                        sx: {
                          '& .MuiAutocomplete-option': {
                            color: 'var(--text-primary-white) !important',
                          },
                        }, // Higher z-index to sit above sidebar/modals
                      },
                      paper: {
                        sx: {
                          color: 'var(--text-primary-white) !important',
                        },
                      },
                    }}
                    sx={inputFieldSx}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder='Enter HTS Code'
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {isHtsLoading ? (
                                <CircularProgress color='inherit' size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </React.Fragment>
                          ),
                        }}
                      />
                    )}
                  />
                ) : (
                  <span>{product?.hts_code || 'N/A'}</span>
                )}
              </div>

              <div className='edit-form-item cols-span-one'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  Country of Origin:
                </span>
                {isEditingDetails ? (
                  <Autocomplete
                    options={countries as COpt[]}
                    getOptionLabel={(option) => displayLabel(option)}
                    isOptionEqualToValue={(opt, val) =>
                      (Boolean(getIso(opt)) && getIso(opt) === getIso(val)) ||
                      getName(opt) === getName(val)
                    }
                    componentsProps={{
                      popper: {
                        sx: {
                          '& .MuiAutocomplete-option': {
                            color: 'var(--text-primary-white) !important',
                          },
                        }, // Higher z-index to sit above sidebar/modals
                      },
                      paper: {
                        sx: {
                          color: 'var(--text-primary-white) !important',
                        },
                      },
                    }}
                    sx={{
                      '::placeholder': { opacity: 1 },
                      background: 'var(--Background-200, var(--input-color))',
                      borderRadius: '8px',
                      height: '45px',
                      '& .MuiAutocomplete-input': {
                        color: 'var(--Text-Main, var(--input-text-color))',
                        fontFamily: 'var(--font-family)',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '24px',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        border: 'none',
                        background: 'var(--Background-200, var(--input-color))',
                        borderRadius: '8px',
                        height: '45px',
                      },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        border: '1px solid var(--secondary-color)',
                        height: '45px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'var(--secondary-color)',
                        },
                        color: 'var(--input-text-color)',
                        '&.Mui-focused': {
                          color: 'var(--focused-text-color)',
                        },
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'var(--focused-text-color)',
                      },
                    }}
                    onChange={(_, newValue) =>
                      handleChange(
                        'country_of_origin',
                        getIso(newValue as COpt),
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder='Select Country of Origin'
                      />
                    )}
                    value={findCountryOption(product?.country_of_origin || '')}
                  />
                ) : (
                  <span>{product?.country_of_origin || 'N/A'}</span>
                )}
              </div>

              <div className='blank-divider cols-span-four'></div>

              <div className='edit-form-item cols-span-one'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  Hierarchy Type:
                </span>
                {isEditingDetails ? (
                  <CustomSelect
                    id='heirarchy_type'
                    className='screening-input-field-content'
                    fullWidth
                    IconComponent={KeyboardArrowDownIcon}
                    inputProps={{
                      MenuProps: {
                        MenuListProps: {
                          sx: {
                            backgroundColor: 'var(--bg-colors)',
                            color: 'var(--Text-Main, var(--text-primary))',
                          },
                        },
                      },
                    }}
                    value={product?.hierarchy_type || ''}
                    onChange={(e: any) =>
                      handleChange &&
                      handleChange('hierarchy_type', e.target.value)
                    }
                  >
                    <MenuItem value=''>Select hierarchy type</MenuItem>
                    <MenuItem value='Assembly'>Assembly</MenuItem>
                    <MenuItem value='Sub-Assembly'>Sub-Assembly</MenuItem>
                    <MenuItem value='Component'>BOM Item/Component</MenuItem>
                  </CustomSelect>
                ) : (
                  <span>{product?.hierarchy_type || 'N/A'}</span>
                )}
              </div>

              <div className='edit-form-item cols-span-one'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  Category:
                </span>
                {isEditingDetails ? (
                  <CustomSelect
                    id='product-category'
                    className='screening-input-field-content'
                    fullWidth
                    IconComponent={KeyboardArrowDownIcon}
                    inputProps={{
                      MenuProps: {
                        MenuListProps: {
                          sx: {
                            backgroundColor: 'var(--bg-colors)',
                            color: 'var(--Text-Main, var(--text-primary))',
                          },
                        },
                      },
                    }}
                    value={product?.category || ''}
                    onChange={(e: any) =>
                      handleChange && handleChange('category', e.target.value)
                    }
                  >
                    <MenuItem value=''>Select category</MenuItem>
                    {productCategories &&
                      productCategories.map((c) => {
                        return (
                          <MenuItem key={c.name} value={c.name}>
                            {c.name}
                          </MenuItem>
                        );
                      })}
                    {product?.category &&
                      product?.category.trim().length > 0 &&
                      productCategories &&
                      productCategories.filter(
                        (c: IProductCategory) => c.name === product?.category,
                      ) && (
                        <MenuItem value={product?.category}>
                          {product?.category}
                        </MenuItem>
                      )}
                  </CustomSelect>
                ) : (
                  <span>{product?.category || 'N/A'}</span>
                )}
              </div>

              <div className='edit-form-item cols-span-one'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  Manufacturer:
                </span>
                {isEditingDetails ? (
                  <TextField
                    className='screening-input-field-content'
                    sx={inputFieldSx}
                    value={product?.manufacturer || ''}
                    onChange={(e: any) =>
                      handleChange &&
                      handleChange('manufacturer', e.target.value)
                    }
                  />
                ) : (
                  <span>{product?.manufacturer || 'N/A'}</span>
                )}
              </div>

              <div className='edit-form-item cols-span-one'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  Vendor:
                </span>
                {isEditingDetails ? (
                  <TextField
                    className='screening-input-field-content'
                    sx={inputFieldSx}
                    value={product?.vendor || ''}
                    onChange={(e: any) =>
                      handleChange && handleChange('vendor', e.target.value)
                    }
                  />
                ) : (
                  <span>{product?.vendor || 'N/A'}</span>
                )}
              </div>
            </div>

            <div className='blank-divider cols-span-four'></div>
            <div className='results-card-header'>
              <RttIcon
                style={{
                  color: 'var(--action-color)',
                }}
              />{' '}
              Description Details
            </div>

            <div className='export-divider cols-span-four'></div>

            <div
              className='widget-content cols-four'
              style={{ display: 'grid' }}
            >
              <div className='edit-form-item cols-span-four'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  Product Description:
                </span>
                {isEditingDetails ? (
                  <TextField
                    multiline={true}
                    minRows={3}
                    className='screening-input-field-content'
                    sx={textAreaSx}
                    value={product?.description || ''}
                    onChange={(e: any) =>
                      handleChange &&
                      handleChange('description', e.target.value)
                    }
                  />
                ) : (
                  <span>{product?.description || 'N/A'}</span>
                )}
              </div>

              <div className='blank-divider cols-span-four'></div>

              <div className='edit-form-item cols-span-four'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  HTS Description:
                </span>
                <span>
                  {product?.tariff_result?.base?.productDesc || 'N/A'}
                </span>
              </div>

              <div className='blank-divider cols-span-four'></div>

              <div className='edit-form-item cols-span-four'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  Specifications:
                </span>
                {isEditingDetails ? (
                  <TextField
                    multiline={true}
                    minRows={3}
                    className='screening-input-field-content'
                    sx={textAreaSx}
                    value={product?.specifications || ''}
                    onChange={(e: any) =>
                      handleChange &&
                      handleChange('specifications', e.target.value)
                    }
                  />
                ) : (
                  <span>{product?.specifications || 'N/A'}</span>
                )}
              </div>
            </div>

            <div className='blank-divider cols-span-four'></div>

            <div className='results-card-header'>
              <PlaylistAddIcon
                style={{
                  color: 'var(--action-color)',
                }}
              />{' '}
              Additional HTS Codes
              {isEditingDetails && (
                <AddBoxIcon
                  style={{
                    color: 'var(--action-color)',
                    marginLeft: '5px',
                    lineHeight: '20px',
                  }}
                  onClick={addHTSCode}
                />
              )}
            </div>

            <div className='export-divider cols-span-four'></div>

            <div
              className='widget-content cols-four'
              style={{ display: 'grid' }}
            >
              <div
                className='edit-form-item cols-span-four cols-three'
                style={{ display: 'grid' }}
              >
                {product?.additional_hts_codes &&
                  product?.additional_hts_codes.length > 0 &&
                  product?.additional_hts_codes.map(
                    (code: any, index: number) => (
                      <>
                        <div className='blank-divider cols-span-four'></div>
                        <div className='edit-form-item cols-span-one'>
                          <span
                            style={{ fontWeight: '600', marginRight: '10px' }}
                          >
                            HTS:
                          </span>
                          {isEditingDetails ? (
                            <Autocomplete
                              freeSolo
                              options={htsOptionsAdditional[`${index}`] ?? []}
                              loading={isHtsLoading}
                              // Format the selected code with dots inside the input box
                              getOptionLabel={(option) =>
                                typeof option === 'string'
                                  ? option
                                  : formatHtsPretty(option.code)
                              }
                              renderOption={(props, option) => (
                                <li {...props} key={option.code}>
                                  {option.label}
                                </li>
                              )}
                              filterOptions={filterHtsOptions}
                              value={
                                code.hts_code
                                  ? (
                                      htsOptionsAdditional[`${index}`] || []
                                    ).find(
                                      (o) =>
                                        o.code ===
                                        code.hts_code.replace(/\D/g, ''),
                                    ) || {
                                      code: code.hts_code.replace(/\D/g, ''),
                                      label: code.hts_code,
                                    }
                                  : null
                              }
                              isOptionEqualToValue={(option, val) =>
                                option.code === val.code
                              }
                              inputValue={code?.hts_code}
                              //eslint-disable-next-line
                              onInputChange={async (
                                event,
                                newInputValue,
                                reason,
                              ) => {
                                // 1. Fully wipe everything on clear
                                if (reason === 'clear') {
                                  handleChange('hts_code', '');
                                  setHtsOptionsAdditional({
                                    ...htsOptionsAdditional,
                                    index: [],
                                  });
                                  setFetchedPrefix('');
                                  return;
                                }

                                handleAdditionalHTSCodeChanges(
                                  'hts_code',
                                  newInputValue,
                                  index,
                                );

                                // 2. THIS FIXES THE RESET BUG:
                                // Keep the actual form state in sync with backspaces/typing
                                if (reason === 'input') {
                                  handleAdditionalHTSCodeChanges(
                                    'hts_code',
                                    newInputValue.replace(/\D/g, ''),
                                    index,
                                  );
                                }

                                const digits = newInputValue.replace(/\D/g, '');

                                if (digits.length >= 6) {
                                  const prefix = digits.slice(0, 6);

                                  if (prefix !== fetchedPrefix) {
                                    setFetchedPrefix(prefix);
                                    setIsHtsLoading(true);
                                    try {
                                      const formattedPrefix =
                                        formatHtsPretty(prefix);
                                      const res =
                                        await DatahubAPI.retrieveHTSCode({
                                          code: formattedPrefix,
                                          country:
                                            code?.country_of_origin || '',
                                        });

                                      const hits =
                                        res?.result?.result?.body?.hits?.hits ||
                                        [];
                                      const optionsMap = new Map();

                                      hits.forEach((hit: any) => {
                                        const src = hit._source;
                                        if (src && src['HS Code']) {
                                          const rawCode = String(src['HS Code'])
                                            .split(' - ')[0]
                                            .trim();
                                          const d = rawCode.replace(/\D/g, '');

                                          if (d.length >= 10) {
                                            const code10 = d.slice(0, 10);
                                            if (!optionsMap.has(code10)) {
                                              const pretty =
                                                formatHtsPretty(code10);
                                              const desc =
                                                src['Product Name'] || '';
                                              optionsMap.set(code10, {
                                                code: code10,
                                                label: `${pretty} - ${desc}`,
                                              });
                                            }
                                          }
                                        }
                                      });
                                      setHtsOptionsAdditional({
                                        ...htsOptionsAdditional,
                                        [index]: Array.from(
                                          optionsMap.values(),
                                        ),
                                      });
                                    } catch (e) {
                                      console.error(
                                        'Error fetching HTS prefix options',
                                        e,
                                      );
                                    } finally {
                                      setIsHtsLoading(false);
                                    }
                                  }
                                } else if (digits.length < 6) {
                                  setHtsOptions([]);
                                  setFetchedPrefix('');
                                }
                              }}
                              //eslint-disable-next-ine
                              onChange={(event, newValue) => {
                                if (typeof newValue === 'string') {
                                  handleAdditionalHTSCodeChanges(
                                    'hts_code',
                                    newValue.replace(/\D/g, ''),
                                    index,
                                  );
                                } else if (newValue && newValue.code) {
                                  handleAdditionalHTSCodeChanges(
                                    'hts_code',
                                    newValue.code,
                                    index,
                                  );
                                } else {
                                  handleChange('hts_code', '');
                                  handleAdditionalHTSCodeChanges(
                                    'hts_code',
                                    '',
                                    index,
                                  );
                                }
                              }}
                              componentsProps={{
                                popper: {
                                  sx: {
                                    '& .MuiAutocomplete-option': {
                                      color:
                                        'var(--text-primary-white) !important',
                                    },
                                  }, // Higher z-index to sit above sidebar/modals
                                },
                                paper: {
                                  sx: {
                                    color:
                                      'var(--text-primary-white) !important',
                                  },
                                },
                              }}
                              sx={inputFieldSx}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder='Enter HTS Code'
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <React.Fragment>
                                        {isHtsLoading ? (
                                          <CircularProgress
                                            color='inherit'
                                            size={20}
                                          />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                      </React.Fragment>
                                    ),
                                  }}
                                />
                              )}
                            />
                          ) : (
                            <span>{code.hts_code || 'N/A'}</span>
                          )}
                        </div>
                        <div className='edit-form-item cols-span-one'>
                          <span
                            style={{ fontWeight: '600', marginRight: '10px' }}
                          >
                            COO:
                          </span>
                          {isEditingDetails ? (
                            <Autocomplete
                              options={countries as COpt[]}
                              getOptionLabel={(option) => displayLabel(option)}
                              isOptionEqualToValue={(opt, val) =>
                                (Boolean(getIso(opt)) &&
                                  getIso(opt) === getIso(val)) ||
                                getName(opt) === getName(val)
                              }
                              componentsProps={{
                                popper: {
                                  sx: {
                                    '& .MuiAutocomplete-option': {
                                      color:
                                        'var(--text-primary-white) !important',
                                    },
                                  }, // Higher z-index to sit above sidebar/modals
                                },
                                paper: {
                                  sx: {
                                    color:
                                      'var(--text-primary-white) !important',
                                  },
                                },
                              }}
                              sx={{
                                '::placeholder': { opacity: 1 },
                                background:
                                  'var(--Background-200, var(--input-color))',
                                borderRadius: '8px',
                                height: '45px',
                                '& .MuiAutocomplete-input': {
                                  color:
                                    'var(--Text-Main, var(--input-text-color))',
                                  fontFamily: 'var(--font-family)',
                                  fontSize: '14px',
                                  fontWeight: 400,
                                  lineHeight: '24px',
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                  border: 'none',
                                  background:
                                    'var(--Background-200, var(--input-color))',
                                  borderRadius: '8px',
                                  height: '45px',
                                },
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                  border: '1px solid var(--secondary-color)',
                                  height: '45px',
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--secondary-color)',
                                  },
                                  color: 'var(--input-text-color)',
                                  '&.Mui-focused': {
                                    color: 'var(--focused-text-color)',
                                  },
                                },
                                '& .MuiSvgIcon-root': {
                                  color: 'var(--focused-text-color)',
                                },
                              }}
                              onChange={(_, newValue) =>
                                handleAdditionalHTSCodeChanges(
                                  'country_of_origin',
                                  getIso(newValue as COpt),
                                  index,
                                )
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder='Select Country of Origin'
                                />
                              )}
                              value={findCountryOption(
                                code?.country_of_origin || '',
                              )}
                            />
                          ) : (
                            <span>{code.country_of_origin || 'N/A'}</span>
                          )}
                        </div>
                        <div
                          className='edit-form-item cols-span-one'
                          style={{
                            width: '50px',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {isEditingDetails && (
                            <DeleteIcon
                              sx={{
                                color: 'var(--action-color)',
                                marginLeft: '5px',
                                verticalAlign: 'middle',
                              }}
                              onClick={() => deleteHTSCode(index)}
                            />
                          )}
                        </div>
                      </>
                    ),
                  )}
              </div>
            </div>
            <div className='blank-divider cols-span-four'></div>
            <br />
            <div className='results-card-header'>
              <AttachMoney
                style={{
                  color: 'var(--action-color)',
                  height: '20',
                }}
              />{' '}
              Tariff Details
            </div>

            <div className='export-divider cols-span-four'></div>

            <div
              className='widget-content cols-four'
              style={{ display: 'grid' }}
            >
              <div className='edit-form-item cols-span-one'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  $ Value:
                </span>
                {isEditingDetails ? (
                  <TextField
                    className='screening-input-field-content'
                    sx={inputFieldSx}
                    value={product?.product_value || ''}
                    onChange={(e: any) =>
                      handleChange &&
                      handleChange('product_value', e.target.value)
                    }
                  />
                ) : (
                  <span>{product?.product_value || 'N/A'}</span>
                )}
              </div>

              <div className='edit-form-item cols-span-one'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  Country of Smelt:
                </span>
                {isEditingDetails ? (
                  <Autocomplete
                    options={countries as COpt[]}
                    getOptionLabel={(option) => displayLabel(option)}
                    isOptionEqualToValue={(opt, val) =>
                      (Boolean(getIso(opt)) && getIso(opt) === getIso(val)) ||
                      getName(opt) === getName(val)
                    }
                    componentsProps={{
                      popper: {
                        sx: {
                          '& .MuiAutocomplete-option': {
                            color: 'var(--text-primary-white) !important',
                          },
                        }, // Higher z-index to sit above sidebar/modals
                      },
                      paper: {
                        sx: {
                          color: 'var(--text-primary-white) !important',
                        },
                      },
                    }}
                    sx={{
                      '::placeholder': { opacity: 1 },
                      background: 'var(--Background-200, var(--input-color))',
                      borderRadius: '8px',
                      height: '45px',
                      '& .MuiAutocomplete-input': {
                        color: 'var(--Text-Main, var(--input-text-color))',
                        fontFamily: 'var(--font-family)',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '24px',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        border: 'none',
                        background: 'var(--Background-200, var(--input-color))',
                        borderRadius: '8px',
                        height: '45px',
                      },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        border: '1px solid var(--secondary-color)',
                        height: '45px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'var(--secondary-color)',
                        },
                        color: 'var(--input-text-color)',
                        '&.Mui-focused': {
                          color: 'var(--focused-text-color)',
                        },
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'var(--focused-text-color)',
                      },
                    }}
                    onChange={(_, newValue) =>
                      handleChange(
                        'country_of_smelt_pour',
                        getIso(newValue as COpt),
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder='Select Country of Smelt'
                      />
                    )}
                    value={findCountryOption(
                      product?.country_of_smelt_pour || '',
                    )}
                  />
                ) : (
                  <span>{product?.country_of_smelt_pour || 'N/A'}</span>
                )}
              </div>

              <div className='blank-divider cols-span-four'></div>

              <div className='edit-form-item cols-span-one'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  Copper Value:
                </span>
                {isEditingDetails ? (
                  <TextField
                    className='screening-input-field-content'
                    sx={inputFieldSx}
                    value={product?.copper_value || ''}
                    onChange={(e: any) =>
                      handleChange &&
                      handleChange('copper_value', e.target.value)
                    }
                  />
                ) : (
                  <span>{product?.copper_value || 'N/A'}</span>
                )}
              </div>

              <div className='edit-form-item cols-span-one'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  Aluminum Value:
                </span>
                {isEditingDetails ? (
                  <TextField
                    className='screening-input-field-content'
                    sx={inputFieldSx}
                    value={product?.aluminum_value || ''}
                    onChange={(e: any) =>
                      handleChange &&
                      handleChange('aluminum_value', e.target.value)
                    }
                  />
                ) : (
                  <span>{product?.aluminum_value || 'N/A'}</span>
                )}
              </div>

              <div className='edit-form-item cols-span-one'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  Steel Value:
                </span>
                {isEditingDetails ? (
                  <TextField
                    className='screening-input-field-content'
                    sx={inputFieldSx}
                    value={product?.steel_value || ''}
                    onChange={(e: any) =>
                      handleChange('steel_value', e.target.value)
                    }
                  />
                ) : (
                  <span>{product?.steel_value || 'N/A'}</span>
                )}
              </div>

              <div className='blank-divider cols-span-four'></div>

              <div className='edit-form-item cols-span-one'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  Copper Percent:
                </span>
                {isEditingDetails ? (
                  <TextField
                    className='screening-input-field-content'
                    sx={inputFieldSx}
                    value={product?.copper_percent || ''}
                    onChange={(e: any) =>
                      handleChange &&
                      handleChange('copper_percent', e.target.value)
                    }
                  />
                ) : (
                  <span>{product?.copper_percent || 'N/A'}</span>
                )}
              </div>

              <div className='edit-form-item cols-span-one'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  Aluminum Percent:
                </span>
                {isEditingDetails ? (
                  <TextField
                    className='screening-input-field-content'
                    sx={inputFieldSx}
                    value={product?.aluminum_percent || ''}
                    onChange={(e: any) =>
                      handleChange &&
                      handleChange('aluminum_percent', e.target.value)
                    }
                  />
                ) : (
                  <span>{product?.aluminum_percent || 'N/A'}</span>
                )}
              </div>

              <div className='edit-form-item cols-span-one'>
                <span style={{ fontWeight: '600', marginRight: '10px' }}>
                  Steel Percent:
                </span>
                {isEditingDetails ? (
                  <TextField
                    className='screening-input-field-content'
                    sx={inputFieldSx}
                    value={product?.steel_percent || ''}
                    onChange={(e: any) =>
                      handleChange &&
                      handleChange('steel_percent', e.target.value)
                    }
                  />
                ) : (
                  <span>{product?.steel_percent || 'N/A'}</span>
                )}
              </div>
            </div>

            <div className='blank-divider cols-span-four'></div>
            <br />
            <div className='results-card-header'>
              <DifferenceIcon
                style={{
                  color: 'var(--action-color)',
                }}
              />{' '}
              Additional Columns
              {isEditingDetails && (
                <AddBoxIcon
                  style={{ color: 'var(--action-color)', marginLeft: '5px' }}
                  onClick={addAdditionalColumn}
                />
              )}
            </div>

            <div className='export-divider cols-span-four'></div>

            <div
              className='widget-content cols-four'
              style={{ display: 'grid' }}
            >
              {Object.entries(getAdditionalColumns()).length > 0 ? (
                Object.entries(getAdditionalColumns()).map(
                  ([columnKey, columnValue], index) => (
                    <span key={`additional-${index}`}>
                      {isEditingDetails ? (
                        <div className='edit-form-item'>
                          <div
                            className='edit-form-item'
                            style={{ display: 'inline-block' }}
                          >
                            <TextField
                              className='screening-input-field-content'
                              sx={{ ...inputFieldSx, display: 'inline-block' }}
                              value={columnKey || ''}
                              onChange={(e: any) =>
                                handleAdditionalColumnChange(
                                  index,
                                  'key',
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                          <div
                            className='edit-form-item'
                            style={{ display: 'inline-block' }}
                          >
                            <TextField
                              className='screening-input-field-content'
                              sx={{ ...inputFieldSx, display: 'inline-block' }}
                              value={
                                columnValue === null ||
                                columnValue === undefined
                                  ? ''
                                  : String(columnValue)
                              }
                              onChange={(e: any) =>
                                handleAdditionalColumnChange(
                                  index,
                                  'value',
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                          <div
                            className='edit-form-item'
                            style={{ display: 'inline-block' }}
                          >
                            <DeleteIcon
                              sx={{
                                verticalAlign: 'middle',
                              }}
                              onClick={() => deleteAdditionalColumn(index)}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className='edit-form-item cols-span-one'>
                          <span
                            style={{ fontWeight: '600', marginRight: '10px' }}
                          >
                            {columnKey}:{' '}
                          </span>
                          <span>{`${columnValue ?? 'N/A'}`}</span>
                        </div>
                      )}
                    </span>
                  ),
                )
              ) : (
                <span style={{ gridColumn: 'span 4' }}>N/A</span>
              )}
            </div>
          </div>

          <div className='results-card cols-span-three'>
            <div className='results-card-header'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='20'
                height='20'
                viewBox='0 0 20 20'
                fill='none'
              >
                <path
                  d='M15 7.875V5.1C15 3.83988 15 3.20982 14.7548 2.72852C14.539 2.30516 14.1948 1.96095 13.7715 1.74524C13.2902 1.5 12.6601 1.5 11.4 1.5H6.6C5.33988 1.5 4.70982 1.5 4.22852 1.74524C3.80516 1.96095 3.46095 2.30516 3.24524 2.72852C3 3.20982 3 3.83988 3 5.1V12.9C3 14.1601 3 14.7902 3.24524 15.2715C3.46095 15.6948 3.80516 16.039 4.22852 16.2548C4.70982 16.5 5.33988 16.5 6.6 16.5H8.625M16.5 16.5L15.375 15.375M16.125 13.5C16.125 14.9497 14.9497 16.125 13.5 16.125C12.0503 16.125 10.875 14.9497 10.875 13.5C10.875 12.0503 12.0503 10.875 13.5 10.875C14.9497 10.875 16.125 12.0503 16.125 13.5Z'
                  stroke='var(--action-color)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>{' '}
              Customs Flags
            </div>

            <LensesTable
              tab={''}
              entity={supplier && supplier?.name}
              cols={<LensColumns />}
              passedDownResults={passedDownResults}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              setCurLevel={setCurLevel}
              setIsHistoryModalOpen={undefined}
              setShareModal={undefined}
              setIsCommentsModalOpen={undefined}
              setIsFrequencyModalOpen={undefined}
              setIsApprovalModalOpen={undefined}
              setScreenParamsModal={undefined}
              setNewTab={undefined}
              setCurrentPage={setCurrentPage}
              curVersion={undefined}
              activeFilter={activeFilter}
              curLevel={curLevel}
              setAISums={undefined}
              removeHit={removeHit}
              handleVisClick={undefined}
              cur_case={mockCase}
              isBulkTicket={false}
              handleCSV={undefined}
              updateFilters={undefined}
              resetTable={undefined}
              newTab={false}
              currentPage={currentPage}
              currentItems={currentItems}
              setCurrentItems={setCurrentItems}
              removeTrigger={false}
              isLoading={isLoading || isScreeningCurrently || isSavingDetails}
              isScreeningInProgress={isScreeningCurrently}
              screeningProgress={screeningProgress}
              isProduct={true}
            />
          </div>

          <div className='results-card cols-span-three'>
            <div className='results-card-header'>
              <AttachMoney
                style={{
                  color: 'var(--action-color)',
                  height: '20',
                }}
              />{' '}
              Tariff & Duty Summary
              <div className='module-header-btns' style={{ float: 'right' }}>
                <button
                  type='button'
                  className='btn-new-automate'
                  onClick={() => setIsTariffHistoryOpen(true)}
                >
                  <div className='modal-btn-text'>Tariff History</div>
                </button>
                <button
                  type='button'
                  className='btn-new-automate'
                  onClick={() => setIsEditTariffModalOpen(true)}
                >
                  <div className='modal-btn-text'>Edit Tariff Inputs</div>
                </button>
              </div>
            </div>
            <TariffResultsComp
              primary={tariffFullResults?.inputs?.htsCode || ''}
              country={tariffFullResults?.inputs?.originCountry || ''}
              productVal={tariffFullResults?.inputs?.productValue || 0.0}
              effectDates={
                tariffFullResults?.inputs?.entryDate
                  ? new Date(tariffFullResults?.inputs?.entryDate)
                  : null
              }
              loadDates={
                tariffFullResults?.inputs?.loadingDate
                  ? new Date(tariffFullResults?.inputs?.loadingDate)
                  : null
              }
              inputs={tariffFullResults?.inputs || null}
              loadingCountryValue={product?.country_or_origin}
              // Records keyed by unit and by metal line, from the same saved
              // snapshot as the inputs above. The product's own product_value
              // and material_pct are plain numbers, which the engine rejects.
              amountInputsValue={tariffFullResults?.inputs?.amountInputs}
              materialPctByKeyValue={tariffFullResults?.inputs?.materialPctByKey}
              isExpands={true}
            />
          </div>
        </div>

        <div className='results-top-grid-two' style={{ marginTop: '16px' }}>
          <div className='results-card'>
            <div className='results-card-header'>
              <DynamicFeedIcon style={{ color: 'var(--action-color)' }} />{' '}
              {'CROSS Rulings'}
              <div className='module-header-btns' style={{ float: 'right' }}>
                <button
                  type='button'
                  className='btn-new-automate'
                  onClick={loadCrossRulings}
                  disabled={crossLoading}
                  style={crossLoading ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                >
                  <div className='modal-btn-text'>
                    {crossLoading
                      ? 'Loading…'
                      : crossLinks && crossLinks.length > 0
                      ? 'Refresh'
                      : 'Load CROSS Rulings'}
                  </div>
                </button>
              </div>
            </div>
            <div
              className='classify-res-comp'
              style={{ height: 'calc(100% - 40px)' }}
            >
              <div
                style={{
                  height: '100%',
                  width: '100%',
                  overflowY: 'scroll',
                  maxHeight: '300px',
                }}
              >
                {crossLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '24px',
                    }}
                  >
                    <CircularProgress color='primary' size={24} />
                  </div>
                ) : crossLinks && crossLinks.length > 0 ? (
                  <>
                    {(isExpandedCross ? crossLinks : crossLinks.slice(0, 5)).map(
                      (url, index) => (
                        <React.Fragment key={index}>
                          <a
                            href={url}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='class-ticket-cross-rules'
                          >
                            {url}
                          </a>
                          <br />
                        </React.Fragment>
                      ),
                    )}
                    {crossLinks.length > 5 && (
                      <span
                        className='expl-notes-btn'
                        onClick={() => setIsExpandedCross(!isExpandedCross)}
                      >
                        {!isExpandedCross ? 'Read More' : 'Read Less'}
                      </span>
                    )}
                  </>
                ) : (
                  <span
                    className='subtitle'
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {'No CROSS rulings loaded yet. Click "Load CROSS Rulings" to fetch matching customs rulings for this product.'}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className='results-card'>
            <div className='results-card-header'>
              <NoAccountsIcon style={{ color: 'var(--action-color)' }} />{' '}
              {'Entity Risk Screening'}
            </div>
            <div
              className='widget-content classify-res-comp'
              style={{ height: 'calc(100% - 40px)' }}
            >
              <div
                style={{
                  height: '100%',
                  width: '100%',
                  overflowY: 'scroll',
                  maxHeight: '300px',
                }}
              >
                {deniedPartyHits.docs?.length > 0 ? (
                  <ul>
                    {(isExpandedDenied
                      ? deniedPartyHits.docs
                      : deniedPartyHits.docs.slice(0, 3)
                    ).map((hit: any, index: number) => (
                      <li
                        key={`denied-party-${index}`}
                        style={{ marginBottom: '10px' }}
                      >
                        {hit.Agency}
                        {' - '}
                        {hit['Name of Entity']}
                        <br />
                        {hit['Source Information URL'] &&
                          hit['Source Information URL']
                            .split(/\s*\|\s*/)
                            .map((url: string, idx: number) => (
                              <React.Fragment key={idx}>
                                <a
                                  href={url}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='class-ticket-cross-rules'
                                >
                                  {url}
                                </a>
                              </React.Fragment>
                            ))}
                      </li>
                    ))}
                  </ul>
                ) : (
                  'No Denied Party Matches Found'
                )}
                {deniedPartyHits.docs && deniedPartyHits.docs?.length > 3 && (
                  <span
                    className='expl-notes-btn'
                    onClick={() => setIsExpandedDenied(!isExpandedDenied)}
                  >
                    {!isExpandedDenied ? 'Read More' : 'Read Less'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='results-top-grid' style={{ marginTop: '16px' }}>
          <div className='results-card cols-span-three'>
            <div className='results-card-header'>
              <FolderOpenIcon style={{ color: 'var(--action-color)' }} />{' '}
              Documents
              <div className='module-header-btns' style={{ float: 'right' }}>
                <button
                  type='button'
                  className='btn-new-automate'
                  style={{ float: 'right' }}
                  onClick={() => setIsFileUploadOpen(true)}
                >
                  <div className='modal-btn-text'>Upload Documents</div>
                </button>
              </div>
            </div>
            <MasterTable
              cols={
                <UploadColumns
                  sortData={handleSortDocs}
                  sortConfig={{ key: sortByDocs, direction: sortOrderDocs }}
                />
              }
              suppliersData={uploads}
              selectedItems={[]}
              setSelectedItems={(items: any[]) => items}
              loading={isLoading}
              currentPage={paginationDocs.currentPage}
              totalPages={paginationDocs.totalPages}
              onPageChange={handlePageChangeDocs}
              customRowComponent={(propsRow: any) => (
                <UploadRow
                  keyVal={propsRow.transaction.id}
                  transaction={propsRow.transaction}
                />
              )}
            />
          </div>
        </div>
        <div className='results-top-grid-two' style={{ marginTop: '16px' }}>
          <div className='results-card'>
            <div className='results-card-header'>
              <LanguageIcon
                style={{
                  color: 'var(--action-color)',
                  height: '20',
                }}
              />{' '}
              Country of Origin Breakdown
            </div>
            <span>Component value by country</span>
            {Object.entries(countryCounts).map(([country, count]) => (
              <div
                key={country}
                style={{ marginBottom: '15px', marginTop: '15px' }}
              >
                <div>
                  <b>{country}</b>{' '}
                  <span style={{ float: 'right', marginRight: '15%' }}>
                    ${countryValues[country].toFixed(2)}
                  </span>
                </div>
                <BarGraph
                  currentValue={count}
                  totalValue={productsData.length}
                  color={'var(--count-color)'}
                />
                <div>{count} Components</div>
              </div>
            ))}
          </div>

          <div className='results-card'>
            <div className='results-card-header'>
              <SegmentIcon
                style={{
                  color: 'var(--action-color)',
                  height: '20',
                }}
              />{' '}
              HTS Codes
            </div>
            <span>List of {productsData?.length || 0} of child components</span>
            {productsData.map((item) => (
              <div key={item.id} className='detail-item'>
                <div style={{ width: '100%' }}>
                  <b>{item.hts_code}</b>
                  {' - '} {item.name}{' '}
                </div>
                <div style={{ width: '100%' }}>
                  <span>{item.description}</span>
                  {item.manufacturer && item.manufacturer !== '' && (
                    <>
                      <br />
                      <span>Supplier: {item.manufacturer}</span>
                    </>
                  )}
                  {item.country_of_origin && item.country_of_origin !== '' && (
                    <>
                      <br />
                      <span>Country of Origin: {item.country_of_origin}</span>
                    </>
                  )}
                  {item.number_of_bom && item.number_of_bom !== 0 && (
                    <>
                      <br />
                      <span>{item.number_of_bom} sub components</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className='results-top-grid' style={{ marginTop: '16px' }}>
          <div className='results-card cols-span-three'>
            <div className='results-card-header'>
              <CommentIcon
                style={{
                  color: 'var(--action-color)',
                  height: '20',
                }}
              />
              Comments ({comments.length})
            </div>
            <div className='widget-content'>
              <textarea
                value={commentContent}
                onChange={(e) => setCommentContent(e.target.value)}
                placeholder='Add Comment...'
                className='comments-panel-footer-cont-action-input-cont'
                style={{
                  width: '100%',
                  height: '80px',
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddComment();
                  }
                }}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '8px',
                }}
              >
                <span className='comments-panel-body-section-feed-cont-msg-text'>
                  Press Enter to submit
                </span>
                <button
                  onClick={handleAddComment}
                  disabled={!commentContent.trim()}
                  className='btn-new-automate'
                >
                  <div className='modal-btn-text'>Add Comment</div>
                </button>
              </div>

              {/* Gap between comment input and comments list */}
              <div style={{ margin: '4px 0' }}></div>

              {/* Comments List - Bottom Section */}
              <div className='comment-container-top'>
                {isLoadingComments ? (
                  <div style={{ textAlign: 'center', padding: '16px' }}>
                    Loading comments...
                  </div>
                ) : !Array.isArray(comments) || comments.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '16px' }}>
                    No comments yet.
                  </div>
                ) : (
                  [...comments]
                    .map((comment) => (
                      <div
                        key={comment.id}
                        style={{
                          padding: '0px 16px',
                          borderBottom: '1px solid #2E3036',
                        }}
                      >
                        <div className='comments-panel-body-section-feed-cont-text-sec'>
                          <h2
                            className='hit-title'
                            style={{ color: 'rgb(72, 149, 239)' }}
                          >
                            {comment.author_name || 'Anonymous'}
                          </h2>
                          <p>
                            {comment.created_at
                              ? getDateDifference(comment.created_at)
                              : 'Just now'}
                          </p>
                          {(cur_user?.basics?.id === comment.author_id ||
                            cur_user?.isAdmin) && (
                            <div>
                              {/* <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'><path d='M9.99998 16.6667H17.5M2.5 16.6667H3.89545C4.3031 16.6667 4.50693 16.6667 4.69874 16.6206C4.8688 16.5798 5.03138 16.5125 5.1805 16.4211C5.34869 16.318 5.49282 16.1739 5.78107 15.8856L16.25 5.41669C16.9404 4.72634 16.9404 3.60705 16.25 2.91669C15.5597 2.22634 14.4404 2.22634 13.75 2.91669L3.28105 13.3856C2.9928 13.6739 2.84867 13.818 2.7456 13.9862C2.65422 14.1353 2.58688 14.2979 2.54605 14.468C2.5 14.6598 2.5 14.8636 2.5 15.2713V16.6667Z' stroke='var(--text-classify)' strokeWidth='1.66667' strokeLinecap='round' strokeLinejoin='round'/>*/}
                              {/* </svg>*/}
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                onClick={() => {
                                  handleDeleteComment(comment.id);
                                }}
                                style={{ cursor: 'pointer' }}
                                width='20'
                                height='20'
                                viewBox='0 0 20 20'
                                fill='none'
                              >
                                <path
                                  d='M7.5 2.5H12.5M2.5 5H17.5M15.8333 5L15.2489 13.7661C15.1612 15.0813 15.1174 15.7389 14.8333 16.2375C14.5833 16.6765 14.206 17.0294 13.7514 17.2497C13.235 17.5 12.5759 17.5 11.2578 17.5H8.74221C7.42409 17.5 6.76503 17.5 6.24861 17.2497C5.79396 17.0294 5.41674 16.6765 5.16665 16.2375C4.88259 15.7389 4.83875 15.0813 4.75107 13.7661L4.16667 5M8.33333 8.75V12.9167M11.6667 8.75V12.9167'
                                  stroke='var(--text-classify)'
                                  strokeWidth='1.66667'
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                ></path>
                              </svg>
                            </div>
                          )}
                        </div>
                        <p className='screening-ai-sum-result-text'>
                          {comment.content}
                        </p>
                      </div>
                    ))
                    .reverse()
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {shareModalOpen && (
        <Formik
          initialValues={{
            sharedWith: [],
            status: 'draft',
          }}
          onSubmit={async (values) => {
            try {
              if (!supplier?.id) {
                enqueueSnackbar('Item ID is missing', { variant: 'error' });
                return;
              }

              const sharePayload = {
                sharedWith: values.sharedWith || [],
                status: values.status,
              };

              const result = await updateProductMutation.mutateAsync({
                id: supplier?.id,
                productData: sharePayload,
              });

              if (result?.success) {
                enqueueSnackbar('Product shared successfully', {
                  variant: 'success',
                });
                setTrigger(!trigger);
                await runScreen();
              } else {
                enqueueSnackbar(result?.message || 'Error sharing product', {
                  variant: 'error',
                });
              }
            } catch (error) {
              console.error('Error sharing item:', error);
              enqueueSnackbar('Error sharing item', { variant: 'error' });
            }
          }}
        >
          {({ values, setFieldValue, submitForm }) => (
            <Form key={shareModalOpen ? 'open-share' : 'closed-shared'}>
              <Modal
                open={shareModalOpen}
                onClose={() => {
                  setShareShowModalOpen(false);
                }}
              >
                <div>
                  <ShareModal
                    userOpts={userOpts}
                    submitForm={submitForm}
                    setShareOpen={setShareShowModalOpen}
                    onSuccess={() => {
                      setTrigger(!trigger);
                      return;
                    }}
                    type={
                      supplier && supplier?.type === 'supplier'
                        ? 'supplier'
                        : 'product'
                    }
                    curUser={cur_user}
                    itemId={supplier ? supplier?.id : ''}
                    item={supplier}
                    values={values}
                    setFieldValue={setFieldValue}
                  />
                </div>
              </Modal>
            </Form>
          )}
        </Formik>
      )}
      <TariffHistoryModal
        open={isTariffHistoryOpen}
        handleClose={() => setIsTariffHistoryOpen(false)}
        databaseProductId={
          supplier?.association_id || supplier?.database_product_id || supplier?.id
        }
        productName={supplier?.name || product?.name}
      />
      <Modal
        open={isEditTariffModalOpen}
        onClose={() => setIsEditTariffModalOpen(false)}
      >
        <div
          className='widget-item product-screen-item'
          style={{
            width: '900px',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            height: 'fit-content',
            padding: '10px',
            margin: 'auto',
            verticalAlign: 'middle',
          }}
        >
          <div className='widget-header'>
            <svg
              width='22'
              height='22'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M10.5 9H15M10.5 12H15M10.5 15H15M3 3.75C3 3.55109 3.07902 3.36032 3.21967 3.21967C3.36032 3.07902 3.55109 3 3.75 3H14.25C14.4489 3 14.6397 3.07902 14.7803 3.21967C14.921 3.36032 15 3.55109 15 3.75V5.25C15 5.44891 14.921 5.63968 14.7803 5.78033C14.6397 5.92098 14.4489 6 14.25 6H3.75C3.55109 6 3.36032 5.92098 3.21967 5.78033C3.07902 5.63968 3 5.44891 3 5.25V3.75ZM3 9.75C3 9.55109 3.07902 9.36032 3.21967 9.21967C3.36032 9.07902 3.55109 9 3.75 9H6.75C6.94891 9 7.13968 9.07902 7.28033 9.21967C7.42098 9.36032 7.5 9.55109 7.5 9.75V14.25C7.5 14.4489 7.42098 14.6397 7.28033 14.7803C7.13968 14.921 6.94891 15 6.75 15H3.75C3.55109 15 3.36032 14.921 3.21967 14.7803C3.07902 14.6397 3 14.4489 3 14.25V9.75Z'
                stroke='var(--action-color)'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            Tariff Details
            <div className='module-header-btns' style={{ float: 'right' }}>
              <button
                type='button'
                className='btn-new-automate'
                onClick={handleDetailsSave}
                disabled={
                  isSavingDetails || isCheckingSubscription || !userHasMonitor
                }
              >
                {isSavingDetails ? (
                  <>
                    <CircularProgress
                      size={16}
                      color='inherit'
                      style={{ marginRight: '8px' }}
                    />
                    <div className='modal-btn-text'>Saving...</div>
                  </>
                ) : (
                  <div className='modal-btn-text'>Save Changes</div>
                )}
              </button>
              <button
                type='button'
                className='btn-new-automate'
                onClick={() => setIsEditTariffModalOpen(false)}
              >
                <div className='modal-btn-text'>Cancel</div>
              </button>
            </div>
          </div>
          <div className='blank-divider cols-span-three'></div>
          <div
            className='widget-content cols-three'
            style={{ display: 'grid', marginTop: '10px' }}
          >
            <div className='edit-form-item cols-span-one'>
              <span style={{ fontWeight: '600', marginRight: '10px' }}>
                Name:
              </span>
              <TextField
                className='screening-input-field-content'
                sx={inputFieldSx}
                value={product?.name || ''}
                onChange={(e: any) =>
                  handleChange && handleChange('name', e.target.value)
                }
              />
            </div>

            <div className='edit-form-item cols-span-one'>
              <span style={{ fontWeight: '600', marginRight: '10px' }}>
                HTS Code:
              </span>
              <Autocomplete
                freeSolo
                options={htsOptions}
                loading={isHtsLoading}
                // Format the selected code with dots inside the input box
                getOptionLabel={(option) =>
                  typeof option === 'string'
                    ? option
                    : formatHtsPretty(option.code)
                }
                renderOption={(props, option) => (
                  <li {...props} key={option.code}>
                    {option.label}
                  </li>
                )}
                filterOptions={filterHtsOptions}
                value={
                  product?.hts_code
                    ? htsOptions.find(
                        (o) => o.code === product?.hts_code.replace(/\D/g, ''),
                      ) || {
                        code: product?.hts_code.replace(/\D/g, ''),
                        label: product?.hts_code,
                      }
                    : null
                }
                isOptionEqualToValue={(option, val) => option.code === val.code}
                inputValue={product?.hts_code}
                //eslint-disable-next-line
                onInputChange={async (event, newInputValue, reason) => {
                  // 1. Fully wipe everything on clear
                  if (reason === 'clear') {
                    handleChange('hts_code', '');
                    setHtsOptions([]);
                    setFetchedPrefix('');
                    return;
                  }

                  handleChange('hts_code', newInputValue);

                  // 2. THIS FIXES THE RESET BUG:
                  // Keep the actual form state in sync with backspaces/typing
                  if (reason === 'input') {
                    handleChange('hts_code', newInputValue.replace(/\D/g, ''));
                  }

                  const digits = newInputValue.replace(/\D/g, '');

                  if (digits.length >= 6) {
                    const prefix = digits.slice(0, 6);

                    if (prefix !== fetchedPrefix) {
                      setFetchedPrefix(prefix);
                      setIsHtsLoading(true);
                      try {
                        const formattedPrefix = formatHtsPretty(prefix);
                        const res = await DatahubAPI.retrieveHTSCode({
                          code: formattedPrefix,
                          country: product?.country_of_origin || '',
                        });

                        const hits =
                          res?.result?.result?.body?.hits?.hits || [];
                        const optionsMap = new Map();

                        hits.forEach((hit: any) => {
                          const src = hit._source;
                          if (src && src['HS Code']) {
                            const rawCode = String(src['HS Code'])
                              .split(' - ')[0]
                              .trim();
                            const d = rawCode.replace(/\D/g, '');

                            if (d.length >= 10) {
                              const code10 = d.slice(0, 10);
                              if (!optionsMap.has(code10)) {
                                const pretty = formatHtsPretty(code10);
                                const desc = src['Product Name'] || '';
                                optionsMap.set(code10, {
                                  code: code10,
                                  label: `${pretty} - ${desc}`,
                                });
                              }
                            }
                          }
                        });
                        setHtsOptions(Array.from(optionsMap.values()));
                      } catch (e) {
                        console.error('Error fetching HTS prefix options', e);
                      } finally {
                        setIsHtsLoading(false);
                      }
                    }
                  } else if (digits.length < 6) {
                    setHtsOptions([]);
                    setFetchedPrefix('');
                  }
                }}
                //eslint-disable-next-ine
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') {
                    handleChange('hts_code', newValue.replace(/\D/g, ''));
                  } else if (newValue && newValue.code) {
                    handleChange('hts_code', newValue.code);
                  } else {
                    handleChange('hts_code', '');
                  }
                }}
                componentsProps={{
                  popper: {
                    sx: {
                      '& .MuiAutocomplete-option': {
                        color: 'var(--text-primary-white) !important',
                      },
                    }, // Higher z-index to sit above sidebar/modals
                  },
                  paper: {
                    sx: {
                      color: 'var(--text-primary-white) !important',
                    },
                  },
                }}
                sx={inputFieldSx}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder='Enter HTS Code'
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {isHtsLoading ? (
                            <CircularProgress color='inherit' size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </div>

            <div className='edit-form-item cols-span-one'>
              <span style={{ fontWeight: '600', marginRight: '10px' }}>
                Country of Origin:
              </span>
              <Autocomplete
                options={countries as COpt[]}
                getOptionLabel={(option) => displayLabel(option)}
                isOptionEqualToValue={(opt, val) =>
                  (Boolean(getIso(opt)) && getIso(opt) === getIso(val)) ||
                  getName(opt) === getName(val)
                }
                componentsProps={{
                  popper: {
                    sx: {
                      '& .MuiAutocomplete-option': {
                        color: 'var(--text-primary-white) !important',
                      },
                    }, // Higher z-index to sit above sidebar/modals
                  },
                  paper: {
                    sx: {
                      color: 'var(--text-primary-white) !important',
                    },
                  },
                }}
                sx={{
                  '::placeholder': { opacity: 1 },
                  background: 'var(--Background-200, var(--input-color))',
                  borderRadius: '8px',
                  height: '45px',
                  '& .MuiAutocomplete-input': {
                    color: 'var(--Text-Main, var(--input-text-color))',
                    fontFamily: 'var(--font-family)',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '24px',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    border: 'none',
                    background: 'var(--Background-200, var(--input-color))',
                    borderRadius: '8px',
                    height: '45px',
                  },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    border: '1px solid var(--secondary-color)',
                    height: '45px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--secondary-color)',
                    },
                    color: 'var(--input-text-color)',
                    '&.Mui-focused': {
                      color: 'var(--focused-text-color)',
                    },
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'var(--focused-text-color)',
                  },
                }}
                onChange={(_, newValue) =>
                  handleChange('country_of_origin', getIso(newValue as COpt))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder='Select Country of Origin'
                  />
                )}
                value={findCountryOption(product?.country_of_origin || '')}
              />
            </div>

            <div className='blank-divider cols-span-three'></div>

            <div className='edit-form-item cols-span-one'>
              <span style={{ fontWeight: '600', marginRight: '10px' }}>
                Country of Smelt:
              </span>
              <Autocomplete
                options={countries as COpt[]}
                getOptionLabel={(option) => displayLabel(option)}
                isOptionEqualToValue={(opt, val) =>
                  (Boolean(getIso(opt)) && getIso(opt) === getIso(val)) ||
                  getName(opt) === getName(val)
                }
                componentsProps={{
                  popper: {
                    sx: {
                      '& .MuiAutocomplete-option': {
                        color: 'var(--text-primary-white) !important',
                      },
                    }, // Higher z-index to sit above sidebar/modals
                  },
                  paper: {
                    sx: {
                      color: 'var(--text-primary-white) !important',
                    },
                  },
                }}
                sx={{
                  '::placeholder': { opacity: 1 },
                  background: 'var(--Background-200, var(--input-color))',
                  borderRadius: '8px',
                  height: '45px',
                  '& .MuiAutocomplete-input': {
                    color: 'var(--Text-Main, var(--input-text-color))',
                    fontFamily: 'var(--font-family)',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '24px',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    border: 'none',
                    background: 'var(--Background-200, var(--input-color))',
                    borderRadius: '8px',
                    height: '45px',
                  },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    border: '1px solid var(--secondary-color)',
                    height: '45px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--secondary-color)',
                    },
                    color: 'var(--input-text-color)',
                    '&.Mui-focused': {
                      color: 'var(--focused-text-color)',
                    },
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'var(--focused-text-color)',
                  },
                }}
                onChange={(_, newValue) =>
                  handleChange(
                    'country_of_smelt_pour',
                    getIso(newValue as COpt),
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder='Select Country of Smelt'
                  />
                )}
                value={findCountryOption(product?.country_of_smelt_pour || '')}
              />
            </div>

            <div className='edit-form-item cols-span-one'>
              <span style={{ fontWeight: '600', marginRight: '10px' }}>
                $ Value:
              </span>
              <TextField
                className='screening-input-field-content'
                sx={inputFieldSx}
                value={product?.product_value || ''}
                onChange={(e: any) =>
                  handleChange && handleChange('product_value', e.target.value)
                }
              />
            </div>

            <div className='blank-divider cols-span-three'></div>

            {/* Entry Date */}
            <div className='edit-form-item cols-span-one'>
              <span style={{ fontWeight: '600', marginRight: '10px' }}>
                Entry Date:
              </span>
              <DatePicker<Date>
                // Remove disabled prop
                value={parseDateOnlyToLocalDate(product?.entry_date)}
                onChange={(newValue: Date | null) => {
                  const ed = formatLocalDateToDateOnly(newValue);
                  if (!ed) {
                    handleChange('entry_date', '');
                    return;
                  }
                  if (!product?.loading_date || product?.loading_date === '') {
                    handleChange('loading_date', moment(ed).toISOString());
                  }

                  handleChange('entry_date', moment(ed).toISOString());
                }}
                slotProps={{
                  actionBar: { actions: ['today'] },
                  textField: {
                    placeholder: 'Select Entry Date',
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      height: '45px',
                      '& .MuiInputBase-root': {
                        backgroundColor: 'var(--input-color)',
                        borderRadius: '8px',
                        height: '45px',
                        paddingRight: '8px',
                        color: 'var(--input-text-color)',
                        display: 'flex',
                        alignSelf: 'stretch',
                        border:
                          '1px solid var(--Colors-Border-border-primary, var(--bg-border-db))',
                        background: 'var(--input-color)',
                        boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
                        fontFamily: 'Inter',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: '500',
                        lineHeight: '20px',
                      },
                      '& input': {
                        color: 'var(--input-text-color)',
                        fontSize: '14px',
                        fontWeight: 500,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--secondary-color)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--input-placeholder-color)',
                      },
                      '& .MuiOutlinedInput-root.Mui-error:hover fieldset': {
                        borderColor: 'var(--input-placeholder-color)',
                      },
                      '& .MuiOutlinedInput-root.Mui-focused.Mui-error fieldset':
                        {
                          borderColor: 'var(--input-placeholder-color)',
                        },
                      '& .MuiFormLabel-root': {
                        color: 'var(--focused-text-color)',
                        fontSize: '14px',
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'var(--focused-text-color)',
                      },
                    },
                  },
                }}
              />
            </div>

            {/* Loading Date */}
            <div className='edit-form-item cols-span-one'>
              <span style={{ fontWeight: '600', marginRight: '10px' }}>
                Loading Date
              </span>
              <DatePicker<Date>
                // Remove disabled prop
                value={parseDateOnlyToLocalDate(product?.loading_date)}
                onChange={(newValue: Date | null) => {
                  const ed = formatLocalDateToDateOnly(newValue);
                  if (!ed) {
                    handleChange('entry_date', '');
                    return;
                  }
                  if (!product?.entry_date || product?.entry_date === '') {
                    handleChange('entry_date', moment(ed).toISOString());
                  }

                  handleChange('loading_date', moment(ed).toISOString());
                }}
                slotProps={{
                  actionBar: { actions: ['today'] },
                  textField: {
                    placeholder: 'Select Loading Date',
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      height: '45px',
                      '& .MuiInputBase-root': {
                        backgroundColor: 'var(--input-color)',
                        borderRadius: '8px',
                        height: '45px',
                        paddingRight: '8px',
                        color: 'var(--input-text-color)',
                        display: 'flex',
                        alignSelf: 'stretch',
                        border:
                          '1px solid var(--Colors-Border-border-primary, var(--bg-border-db))',
                        background: 'var(--input-color)',
                        boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
                        fontFamily: 'Inter',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: '500',
                        lineHeight: '20px',
                      },
                      '& input': {
                        color: 'var(--input-text-color)',
                        fontSize: '14px',
                        fontWeight: 500,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--secondary-color)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--input-placeholder-color)',
                      },
                      '& .MuiOutlinedInput-root.Mui-error:hover fieldset': {
                        borderColor: 'var(--input-placeholder-color)',
                      },
                      '& .MuiOutlinedInput-root.Mui-focused.Mui-error fieldset':
                        {
                          borderColor: 'var(--input-placeholder-color)',
                        },
                      '& .MuiFormLabel-root': {
                        color: 'var(--focused-text-color)',
                        fontSize: '14px',
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'var(--focused-text-color)',
                      },
                    },
                  },
                }}
              />
            </div>

            <div className='blank-divider cols-span-three'></div>

            <div className='edit-form-item cols-span-one'>
              <span style={{ fontWeight: '600', marginRight: '10px' }}>
                Copper Value:
              </span>
              <TextField
                className='screening-input-field-content'
                sx={inputFieldSx}
                value={product?.copper_value || ''}
                onChange={(e: any) =>
                  handleChange && handleChange('copper_value', e.target.value)
                }
              />
            </div>

            <div className='edit-form-item cols-span-one'>
              <span style={{ fontWeight: '600', marginRight: '10px' }}>
                Aluminum Value:
              </span>
              <TextField
                className='screening-input-field-content'
                sx={inputFieldSx}
                value={product?.aluminum_value || ''}
                onChange={(e: any) =>
                  handleChange && handleChange('aluminum_value', e.target.value)
                }
              />
            </div>

            <div className='edit-form-item cols-span-one'>
              <span style={{ fontWeight: '600', marginRight: '10px' }}>
                Steel Value:
              </span>
              <TextField
                className='screening-input-field-content'
                sx={inputFieldSx}
                value={product?.steel_value || ''}
                onChange={(e: any) =>
                  handleChange('steel_value', e.target.value)
                }
              />
            </div>

            <div className='blank-divider cols-span-three'></div>

            <div className='edit-form-item cols-span-one'>
              <span style={{ fontWeight: '600', marginRight: '10px' }}>
                Copper Percent:
              </span>
              <TextField
                className='screening-input-field-content'
                sx={inputFieldSx}
                value={product?.copper_percent || ''}
                onChange={(e: any) =>
                  handleChange && handleChange('copper_percent', e.target.value)
                }
              />
            </div>

            <div className='edit-form-item cols-span-one'>
              <span style={{ fontWeight: '600', marginRight: '10px' }}>
                Aluminum Percent:
              </span>
              <TextField
                className='screening-input-field-content'
                sx={inputFieldSx}
                value={product?.aluminum_percent || ''}
                onChange={(e: any) =>
                  handleChange &&
                  handleChange('aluminum_percent', e.target.value)
                }
              />
            </div>

            <div className='edit-form-item cols-span-one'>
              <span style={{ fontWeight: '600', marginRight: '10px' }}>
                Steel Percent:
              </span>
              <TextField
                className='screening-input-field-content'
                sx={inputFieldSx}
                value={product?.steel_percent || ''}
                onChange={(e: any) =>
                  handleChange && handleChange('steel_percent', e.target.value)
                }
              />
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={isChangeStatusModalOpen}
        onClose={() => setIsChangeStatusModalOpen(false)}
      >
        <div>
          <ChangeStatus
            isChangingStatus={isChangingStatus}
            handleClose={() => setIsChangeStatusModalOpen(false)}
            changeStatus={handleStatusUpdate}
            curStatus={currentStatusFlag}
          />
        </div>
      </Modal>

      <Modal open={isAuditModalOpen} onClose={() => setIsAuditModalOpen(false)}>
        <div
          className='audit-add-modal-cont'
          style={{
            width: '800px',
            maxWidth: '800px',
            padding: '0px 10px',
          }}
        >
          <div className='audit-add-modal-cont-head'>
            <div className='activity-cont-frame-left-table-cont-text-sup'>
              <div className='activity-cont-frame-left-table-cont-text-sup-bagde'>
                <div className='activity-cont-frame-left-table-cont-text-sup-bagde-frame'>
                  <div>
                    Audit Log
                    <div
                      className='modal-delete-btn'
                      onClick={() => setIsAuditModalOpen(false)}
                    >
                      <div className='modal-btn-text'>{'X Close'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='audit-doc-tabe-col'>
            <div className='audit-doc-tabe-col-right-cell'>
              <div>
                <div className='audit-doc-tabe-col-left-cell-text'>User</div>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                >
                  <path
                    d='M7.99992 3.33301V12.6663M7.99992 12.6663L12.6666 7.99967M7.99992 12.6663L3.33325 7.99967'
                    stroke='var(--alternative-text-color)'
                    strokeWidth='1.33333'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            </div>
            <div className='audit-doc-tabe-col-right-cell'>
              <div>
                <div className='audit-doc-tabe-col-left-cell-text'>Action</div>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                >
                  <path
                    d='M7.99992 3.33301V12.6663M7.99992 12.6663L12.6666 7.99967M7.99992 12.6663L3.33325 7.99967'
                    stroke='var(--alternative-text-color)'
                    strokeWidth='1.33333'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            </div>
            <div className='audit-doc-tabe-col-right-cell'>
              <div>
                <div className='audit-doc-tabe-col-left-cell-text'>
                  Date & Time
                </div>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                >
                  <path
                    d='M7.99992 3.33301V12.6663M7.99992 12.6663L12.6666 7.99967M7.99992 12.6663L3.33325 7.99967'
                    stroke='var(--alternative-text-color)'
                    strokeWidth='1.33333'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            </div>
          </div>
          <div
            className='activity-cont-frame-left-table-feed'
            style={{ paddingTop: '15px', gap: 'var(--spacing-lg, 15px)' }}
          >
            {isLoadingCurrentAuditLogs ? (
              <div
                className='hit-meta'
                style={{ textAlign: 'center', padding: '20px' }}
              >
                Loading audit logs...
              </div>
            ) : currentAuditLogsError ? (
              <div
                className='hit-meta'
                style={{
                  textAlign: 'center',
                  padding: '20px',
                  color: '#ff6b6b',
                }}
              >
                Error loading audit logs
              </div>
            ) : !currentAuditItems || currentAuditItems.length === 0 ? (
              <div
                className='hit-meta'
                style={{ textAlign: 'center', padding: '20px' }}
              >
                No audit logs found
              </div>
            ) : (
              currentAuditItems.map((activity: any, index: number) => (
                <React.Fragment key={`activity-${index}`}>
                  <AuditLogRow activity={activity} />
                  <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>
                </React.Fragment>
              ))
            )}
          </div>
          <div className='activity-cont-frame-pagination'>
            <div className='activity-cont-frame-pagination-text'>
              Page {currentAuditPage} of {totalAuditPages}
            </div>
            <div className='activity-cont-frame-pagination-action'>
              <div className='activity-cont-frame-pagination-action-prev'>
                <div
                  className='activity-cont-frame-pagination-action-text'
                  onClick={handlePrevAuditPage}
                >
                  Previous
                </div>
              </div>
              <div className='activity-cont-frame-pagination-action-prev'>
                <div
                  className='activity-cont-frame-pagination-action-text'
                  onClick={handleNextAuditPage}
                >
                  Next
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <Modal open={openRules} onClose={handleCloseRules}>
        <div>
          <ComplianceScoringGuidelines setIsOpen={setOpenRules} />
        </div>
      </Modal>
      <AddUploadModal
        database={{ id: product?.database_id, name: product?.database_name }}
        product={product}
        open={isFileUploadOpen}
        handleClose={handleCloseFileUpload}
      />
    </div>
  );
};

export default Results;
